import { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import AdminDashboardLayout from "../../layouts/AdminDashboardLayout";
import BrandLoader from "../../components/BrandLoader";
// ─── Subscriptions & Packages Management Page (Production Ready) ──────────────
import { adminApi } from "../../services/adminApi";
import { notificationAPI } from "../../services/api";
import { packageService } from "../../services/packageService";
import { useDynamicPackages } from "../../hooks/useDynamicPackages";
import {
  FiUserCheck, FiUsers, FiZap, FiCheckCircle, FiStar, FiUser, FiCircle,
  FiDownload, FiFrown, FiCheck, FiX, FiEye, FiEdit,
  FiMoreHorizontal, FiSearch, FiTrendingUp, FiRefreshCw,
  FiCalendar, FiArrowUp, FiFileText, FiCreditCard,
  FiAlertCircle, FiInfo, FiPlus, FiTrash2, FiTag, FiPercent,
  FiChevronDown, FiFilter, FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight, FiAlertTriangle, FiClock, FiLock,
  FiActivity, FiTarget, FiAward, FiMessageSquare, FiShield, FiDownloadCloud, FiSliders,
  FiCornerDownRight
} from "react-icons/fi";

const sendSubscriptionNotification = async ({ userId, title, message }) => {
  if (!userId) return;
  try {
    window.dispatchEvent(new Event("subscription-updated"));
    await notificationAPI.sendNotification({
      recipientUserId: Number(userId),
      recipientGroup: "Individual",
      title: title,
      message: message,
      category: "subscription",
      type: "push"
    }).catch(() => null);

    if ("Notification" in window && Notification.permission === "granted") {
      try {
        new Notification(title, {
          body: message,
          icon: "/favicon.ico",
          badge: "/favicon.ico"
        });
      } catch {
        // silent fallback
      }
    }
  } catch {
    // silent fallback
  }
};
// ─── TOKENS ──────────────────────────────────────────────────────────────────
const T = {
  olive: "#6B8A5A", olive2: "#8BA47A",
  orange: "#F29A0C", orange2: "#E97818",
  dark: "#1F1F1F", mid: "#555", muted: "#AAA", light: "#CCC",
  border: "rgba(239,229,216,0.8)",
  green: "#22C55E", greenDk: "#16A34A",
  red: "#E24B4A", amber: "#F59E0B",
};

const glass = {
  background: "rgba(255,255,255,0.82)",
  border: "1px solid rgba(255,255,255,0.95)",
  backdropFilter: "blur(32px)",
  WebkitBackdropFilter: "blur(32px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.06), inset 0 1px 0 #fff",
};

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const TIER_STYLES = {
  Premium:    { bg:"rgba(242,154,12,0.12)", c:"#C8660A", b:"rgba(242,154,12,0.25)" },
  Standard:   { bg:"rgba(83,74,183,0.10)",  c:"#3C3489", b:"rgba(83,74,183,0.20)"  },
  Basic:      { bg:"rgba(24,95,165,0.10)",  c:"#0C447C", b:"rgba(24,95,165,0.20)"  },
  "YTT Pro":  { bg:"rgba(107,138,90,0.12)", c:"#27500A", b:"rgba(107,138,90,0.25)" },
  "Free Trial":{ bg:"rgba(200,200,200,0.12)", c:"#666",  b:"rgba(200,200,200,0.3)" },
};

const STATUS_COLORS = {
  Active:"#16A34A", Expiring:"#F59E0B",
  Expired:"#E24B4A", Cancelled:"#888", Pending:"#185FA5",
};

const AVATAR_GRADS = [
  "linear-gradient(135deg,#F29A0C,#E97818)",
  "linear-gradient(135deg,#6B8A5A,#8BA47A)",
  "linear-gradient(135deg,#534AB7,#7F77DD)",
  "linear-gradient(135deg,#185FA5,#378ADD)",
  "linear-gradient(135deg,#E24B4A,#A32D2D)",
];

const TIERS = ["Free Trial", "Basic Plan", "Standard Plan", "Premium Plan", "YTT Pro"];

// ─── CUSTOM DROPDOWN COMPONENT (With Search & Dynamic Height) ───────────────────
const CustomDropdown = ({ value, onChange, options, label, icon: Icon, fullWidth = true, className = "", searchable = true, placeholder = "Select..." }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setSearchTerm("");
  }, [open]);

  const selectedOptionObj = options.find(opt => typeof opt === "object" ? String(opt.value) === String(value) : String(opt) === String(value));
  let displayLabel = "";

  if (value === "all") {
    displayLabel = (label === "User Period" || label === "Time Period") ? "All Time Users" : `All ${label || "Option"}s`;
  } else if (selectedOptionObj && typeof selectedOptionObj === "object") {
    displayLabel = selectedOptionObj.label;
  } else if (value) {
    displayLabel = value;
  } else {
    displayLabel = placeholder || `Select ${label || "Option"}`;
  }

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    return options.filter((opt) => {
      const optLabel = typeof opt === "object" ? opt.label : (opt === "all" ? (label === "User Period" ? "All Time Users" : `All ${label || "Option"}s`) : String(opt));
      return String(optLabel).toLowerCase().includes(searchTerm.toLowerCase().trim());
    });
  }, [options, searchTerm, label]);

  return (
    <div className={`relative ${open ? "z-[999]" : "z-10"} ${fullWidth ? "w-full" : "inline-block min-w-[170px]"} ${className}`} ref={dropdownRef}>
      <motion.button
        type="button"
        whileTap={{ scale: 0.985 }}
        onClick={() => setOpen(o => !o)}
        className={`px-4 py-2.5 font-bold text-[13px] flex items-center justify-between gap-2.5 cursor-pointer transition-all border outline-none ${
          fullWidth ? "w-full h-11 rounded-[14px]" : "h-10 rounded-full"
        }`}
        style={{
          background: open ? "#FFFFFF" : "rgba(255,255,255,0.92)",
          backdropFilter: "blur(16px)",
          borderColor: open ? "#F29A0C" : "rgba(239,229,216,0.95)",
          color: value && value !== "all" ? "#1F1F1F" : "#71717A",
          boxShadow: open 
            ? "0 0 0 3.5px rgba(242,154,12,0.18), 0 8px 24px rgba(0,0,0,0.06)" 
            : "0 2px 8px rgba(0,0,0,0.03)"
        }}>
        <div className="flex items-center gap-2.5 min-w-0 truncate">
          {Icon && <Icon size={15} className={`shrink-0 ${open ? "text-amber-600" : "text-amber-500"}`}/>}
          <span className="truncate">{displayLabel}</span>
        </div>
        <FiChevronDown size={15} className={`text-zinc-400 shrink-0 transition-transform duration-250 ${open ? "rotate-180 text-amber-600" : ""}`}/>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className={`ad-dd-menu absolute left-0 z-[1000] p-2 rounded-[20px] border overflow-hidden flex flex-col ${
              fullWidth ? "right-0 min-w-[220px]" : "min-w-[220px] max-w-[320px]"
            } top-[48px] max-h-[340px] shadow-2xl`}
            style={{
              background: "#FFFFFF",
              backdropFilter: "blur(24px)",
              borderColor: "rgba(239,229,216,0.95)",
              boxShadow: "0 25px 60px rgba(0,0,0,0.22)"
            }}>
            {/* Search Bar inside Dropdown Popover */}
            {searchable && options.length > 3 && (
              <div className="p-1 mb-1.5 border-b border-zinc-100/80 sticky top-0 bg-white/90 backdrop-blur-md z-10">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-100/90 border border-zinc-200/70 focus-within:border-amber-500 transition-all">
                  <FiSearch size={13} className="text-zinc-400 shrink-0" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search options..."
                    className="w-full text-[11.5px] bg-transparent outline-none text-zinc-800 placeholder:text-zinc-400 font-medium"
                    onClick={(e) => e.stopPropagation()}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setSearchTerm(""); }}
                      className="text-zinc-400 hover:text-zinc-600 bg-transparent border-none cursor-pointer p-0 flex items-center justify-center"
                    >
                      <FiX size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            <div className="overflow-y-auto flex-1 max-h-[260px] pr-0.5" style={{ scrollbarWidth: "thin", msOverflowStyle: "none" }}>
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-4 text-center text-[11.5px] text-zinc-400 italic">
                  No matching items found
                </div>
              ) : (
                filteredOptions.map(opt => {
                  const optVal = typeof opt === "object" ? opt.value : opt;
                  const optLabel = typeof opt === "object" ? opt.label : (opt === "all" ? (label === "User Period" ? "All Time Users" : `All ${label || "Option"}s`) : String(opt));
                  const isSelected = String(value) === String(optVal);

                  return (
                    <button
                      key={String(optVal)}
                      type="button"
                      onClick={() => {
                        onChange(optVal);
                        setOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-[12px] text-[12.5px] font-bold flex items-center justify-between cursor-pointer transition-all border-none my-0.5 ${
                        isSelected 
                          ? "bg-amber-500 text-white shadow-sm" 
                          : "text-zinc-700 hover:bg-amber-50 hover:text-amber-900"
                      }`}>
                      <span className="truncate">{optLabel}</span>
                      {isSelected && <FiCheckCircle size={14} className="text-white shrink-0 ml-2"/>}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── PAGINATION SUITE (User Management & Diet Plan Manager Unified Design) ───
const PaginationSuite = ({
  currentPage,
  totalPages,
  totalItems,
  startIndex,
  endIndex,
  pageSize,
  itemLabel = "subscriptions",
  onPageChange
}) => {
  const pageSizeNum = pageSize === "all" ? totalItems : Number(pageSize || 10);
  const startNum = startIndex !== undefined ? startIndex : (totalItems > 0 ? (currentPage - 1) * pageSizeNum + 1 : 0);
  const endNum = endIndex !== undefined ? endIndex : (pageSize === "all" ? totalItems : Math.min(currentPage * pageSizeNum, totalItems));

  return (
    <div
      className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t"
      style={{ borderColor: "rgba(239,229,216,0.6)", background: "rgba(255,255,255,0.6)", backdropFilter: "blur(12px)" }}
    >
      <div className="text-[12px] font-bold text-zinc-500">
        Showing <span className="text-zinc-900 font-black">{startNum}</span> to{" "}
        <span className="text-zinc-900 font-black">{endNum}</span> of{" "}
        <span className="text-zinc-900 font-black">{totalItems}</span> {itemLabel}
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Active Page Counter Pill */}
          <span className="text-[11.5px] font-black text-amber-900 bg-amber-100/90 px-3 py-1 rounded-full border border-amber-300 shadow-2xs mr-1">
            Page {currentPage} of {totalPages}
          </span>

          {/* Jump to First Page Button */}
          <motion.button
            whileTap={{ scale: 0.92 }}
            disabled={currentPage <= 1}
            onClick={() => onPageChange(1)}
            title="First Page"
            className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
          >
            <FiChevronsLeft size={14} />
          </motion.button>

          {/* Previous Page Button */}
          <motion.button
            whileTap={{ scale: 0.92 }}
            disabled={currentPage <= 1}
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            title="Previous Page"
            className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
          >
            <FiChevronLeft size={14} /> Prev
          </motion.button>

          {/* Smart Windowed Page Number Buttons */}
          <div className="flex items-center gap-1">
            {(() => {
              let pages = [];
              if (totalPages <= 7) {
                pages = Array.from({ length: totalPages }, (_, i) => i + 1);
              } else if (currentPage <= 4) {
                pages = [1, 2, 3, 4, 5, "...", totalPages];
              } else if (currentPage >= totalPages - 3) {
                pages = [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
              } else {
                pages = [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
              }

              return pages.map((item, idx) => {
                if (item === "...") {
                  return (
                    <span key={`dots-${idx}`} className="px-1 text-zinc-400 font-black text-[12px] select-none">
                      •••
                    </span>
                  );
                }

                const isActive = currentPage === item;
                return (
                  <motion.button
                    key={item}
                    whileTap={{ scale: 0.92 }}
                    onClick={() => onPageChange(item)}
                    className={`w-8 h-8 rounded-lg text-[12px] font-black transition-all cursor-pointer ${
                      isActive
                        ? "text-white shadow-xs"
                        : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                    }`}
                    style={
                      isActive
                        ? {
                            background: `linear-gradient(135deg,#F29A0C,#E97818)`,
                            borderColor: "transparent",
                            boxShadow: `0 4px 12px rgba(242,154,12,0.35)`,
                          }
                        : {}
                    }
                  >
                    {item}
                  </motion.button>
                );
              });
            })()}
          </div>

          {/* Next Page Button */}
          <motion.button
            whileTap={{ scale: 0.92 }}
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            title="Next Page"
            className="px-2.5 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center gap-1 cursor-pointer transition-all shadow-2xs"
          >
            Next <FiChevronRight size={14} />
          </motion.button>

          {/* Jump to Last Page Button */}
          <motion.button
            whileTap={{ scale: 0.92 }}
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(totalPages)}
            title="Last Page"
            className="w-8 h-8 rounded-lg text-[12px] font-bold border border-zinc-200 bg-white text-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-zinc-100 flex items-center justify-center cursor-pointer transition-all shadow-2xs"
          >
            <FiChevronsRight size={14} />
          </motion.button>
        </div>
      )}
    </div>
  );
};

// ─── SMALL COMPONENTS ────────────────────────────────────────────────────────
function TierPill({ tier }) {
  const s = TIER_STYLES[tier] || TIER_STYLES["Free Trial"];
  return (
    <span className="inline-flex items-center text-[10px] font-black px-2.5 py-1 rounded-full"
      style={{ background:s.bg, color:s.c, border:`1px solid ${s.b}` }}>{tier}</span>
  );
}

function StatusPill({ status }) {
  const c = STATUS_COLORS[status] || "#888";
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full"
      style={{ background:`${c}18`, color:c, border:`1px solid ${c}30` }}>
      <span className="w-[5px] h-[5px] rounded-full flex-shrink-0" style={{ background:c }}/>
      {status}
    </span>
  );
}

function UserAvatar({ sub, size = 32, radius = 10 }) {
  const [imgError, setImgError] = useState(false);
  const avatarUrl = !imgError ? (sub?.profileImageUrl || sub?.avatarUrl || sub?.userProfileImage || sub?.user?.profileImageUrl || sub?.user?.avatarUrl) : null;

  if (avatarUrl) {
    return (
      <div className="relative flex-shrink-0 overflow-hidden shadow-sm border border-amber-900/20"
        style={{ width: size, height: size, borderRadius: radius }}>
        <img
          src={avatarUrl}
          alt={sub?.name || "User"}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  const nameStr = sub?.name || sub?.userFullName || "User";
  const initials = sub?.initials || nameStr.substring(0, 2).toUpperCase();
  const grad = sub?.grad || "linear-gradient(135deg, #F29A0C, #E97818)";

  return (
    <div className="relative flex-shrink-0 flex items-center justify-center font-black text-white overflow-hidden shadow-xs"
      style={{ width: size, height: size, borderRadius: radius, background: grad, fontFamily: "'Poppins',sans-serif", fontSize: size * 0.35, position: "relative" }}>
      <div className="absolute inset-0" style={{ background: "linear-gradient(135deg,rgba(255,255,255,0.22),transparent)" }}/>
      <span className="relative z-10">{initials}</span>
    </div>
  );
}

// ─── KPI CARD ─────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, value, label, trend, trendUp = true, iconBg, accentColor, cardBg, delay = 0 }) {
  return (
    <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay }}
      whileHover={{ y:-4, boxShadow:"0 22px 52px rgba(0,0,0,0.10)" }}
      className="ad-kpi-card ad-kpi-flat relative overflow-hidden rounded-[20px] p-4 border"
      style={{ background:cardBg || "rgba(255,255,255,0.85)", borderColor:"rgba(255,255,255,0.9)", transition:"all 0.28s" }}>
      {/* Shine sweep */}
      <div className="ad-kpi-orb absolute top-0 -left-full w-3/5 h-full pointer-events-none"
        style={{ background:"linear-gradient(90deg,transparent,rgba(255,255,255,0.4),transparent)", transition:"left 0.6s" }}
        onMouseEnter={e => e.currentTarget.style.left = "140%"}/>
      <div className="ad-kpi-icon w-9 h-9 rounded-[13px] flex items-center justify-center mb-3" style={{ background:iconBg }}>
        <Icon size={17} style={{ color:"#fff" }}/>
      </div>
      <p className="ad-kpi-val font-black text-[#1F1F1F] mb-1" style={{ fontFamily:"'Poppins',sans-serif", fontSize:26, letterSpacing:"-1.2px", lineHeight:1 }}>{value}</p>
      <p className="ad-kpi-lbl text-[10px] font-bold uppercase tracking-[0.8px] mb-1.5" style={{ color:T.muted }}>{label}</p>
      <p className="ad-kpi-note text-[11px] font-bold flex items-center gap-1" style={{ color:trendUp ? T.greenDk : T.red }}>
        <FiTrendingUp size={11}/>{trend}
      </p>
      <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-b-[20px]" style={{ background:accentColor }}/>
    </motion.div>
  );
}

// ─── DETAIL DRAWER ────────────────────────────────────────────────────────────
function SubDrawer({ sub, packages = [], matchTier, allUserSubs = [], onClose, onAction, showToast }) {
  if (!sub) return null;

  const matchedPkg = packages.find(p => p.id === sub.packageId || (matchTier && matchTier(sub, p.name)));
  const totalTierDays = matchedPkg?.durationInDays || matchedPkg?.durationDays || sub.daysTotal || 30;

  const usedPct = Math.max(0, Math.min(100, 100 - Math.round((sub.daysLeft ?? totalTierDays) / totalTierDays * 100)));
  const timeline = [
    { label:"Subscription activated",   date:sub.start, color:T.olive },
    { label:"Payment received",          date:sub.start, color:T.orange },
    { label:`${sub.tier} plan assigned`, date:sub.start, color:"#185FA5" },
    { label:`Auto-renewal ${sub.autoRenew ? "enabled" : "disabled"}`, date:sub.start, color:sub.autoRenew ? T.greenDk : "#888" },
  ];

  return createPortal(
    <>
      <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
        className="fixed inset-0 z-[99998]" style={{ background: "rgba(15,15,15,0.4)" }} onClick={onClose}/>
      <motion.div initial={{ x:28, opacity:0 }} animate={{ x:0, opacity:1 }}
        exit={{ x:28, opacity:0 }} transition={{ duration:0.35, ease:[0.25,1,0.5,1] }}
        className="ad-drawer fixed right-0 top-0 bottom-0 overflow-y-auto z-[99999] [&::-webkit-scrollbar]:hidden"
        style={{ width:420, background:"rgba(255,253,248,0.97)", borderLeft:`1px solid rgba(239,229,216,0.7)`, boxShadow:"-24px 0 80px rgba(0,0,0,0.15)", scrollbarWidth:"none", msOverflowStyle:"none" }}>

        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b"
          style={{ background:"rgba(255,253,248,0.95)", backdropFilter:"blur(20px)", borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[13px] font-bold text-[#1F1F1F]">Subscription Detail</p>
          <motion.button whileHover={{ background:"rgba(239,229,216,0.9)" }} onClick={onClose}
            className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer"
            style={{ background:"rgba(239,229,216,0.5)", color:"#888" }}>
            <FiX size={15}/>
          </motion.button>
        </div>

        {/* User hero */}
        <div className="px-6 pt-5 pb-5 relative overflow-hidden border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <div className="absolute top-0 right-0 w-40 h-40 pointer-events-none"
            style={{ background:"radial-gradient(circle,rgba(107,138,90,0.12),transparent)", filter:"blur(40px)" }}/>
          <div className="flex gap-3.5 items-start relative">
            <UserAvatar sub={sub} size={60} radius={18}/>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h2 className="font-black text-[17px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", letterSpacing:"-0.4px" }}>{sub.name}</h2>
                <TierPill tier={sub.tier}/>
              </div>
              <p className="text-[12px] mb-2" style={{ color:T.muted }}>{sub.flag} {sub.email}</p>
              <div className="flex gap-2 flex-wrap">
                <StatusPill status={sub.status}/>
                <span className="text-[11px] flex items-center gap-1" style={{ color:T.muted }}>
                  <FiFileText size={11}/> {sub.txId}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Plan details */}
        <div className="px-6 py-5 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3" style={{ color:T.light }}>Plan Details</p>
          <div className="grid grid-cols-2 gap-2.5 mb-4">
            <div className="rounded-[14px] p-3" style={{ background:"rgba(255,244,232,0.7)" }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.6px] mb-1" style={{ color:"#C8660A" }}>Amount Paid</p>
              <p className="font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:20, letterSpacing:"-1px" }}>₹{(sub.amountPaid ?? sub.amount ?? 0).toLocaleString()}</p>
            </div>
            <div className="rounded-[14px] p-3" style={{ background:"rgba(245,250,242,0.7)" }}>
              <p className="text-[10px] font-bold uppercase tracking-[0.6px] mb-1" style={{ color:"#27500A" }}>Days Remaining</p>
              <p className="font-black" style={{ fontFamily:"'Poppins',sans-serif", fontSize:20, letterSpacing:"-1px", color:(sub.daysLeft ?? totalTierDays) < 14 ? T.red : T.dark }}>{sub.daysLeft ?? totalTierDays}d</p>
            </div>
          </div>
          {[["Plan Duration", `${totalTierDays} Days`], ["Start Date",sub.start],["Expiry Date",sub.end],["Payment Method",sub.method],["Transaction ID",sub.txId],["Auto-Renewal",sub.autoRenew ? "Enabled" : "Disabled"]].map(([k,v]) => (
            <div key={k} className="flex justify-between items-center py-2 border-b" style={{ borderColor:"rgba(239,229,216,0.4)" }}>
              <span className="text-[12px]" style={{ color:T.muted }}>{k}</span>
              <span className="text-[13px] font-semibold text-[#1F1F1F]">{v}</span>
            </div>
          ))}
        </div>

        {/* Complete Subscription History for User */}
        {allUserSubs.length > 0 && (
          <div className="px-6 py-4 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
            <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3 text-amber-700">All User Subscription History ({allUserSubs.length})</p>
            <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth:"none", msOverflowStyle:"none" }}>
              {allUserSubs.map((item, idx) => (
                <div key={item.id || idx} className="p-3 rounded-[14px] bg-white border border-amber-950/10 flex items-center justify-between shadow-sm">
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-bold text-[12px] text-zinc-900">{item.tier || item.packageName || "Package"}</span>
                      <TierPill tier={item.tier || item.packageName || "Basic"} />
                    </div>
                    <p className="text-[10px] text-zinc-500">{item.start || "Start"} — {item.end || "End"}</p>
                  </div>
                  <div className="text-right">
                    <span className="font-black text-[13px] text-zinc-900">₹{(item.amountPaid ?? item.amount ?? 0).toLocaleString()}</span>
                    <p className="text-[9px] font-bold text-emerald-600">{item.status || "Active"}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Progress */}
        <div className="px-6 py-4 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <div className="flex justify-between text-[11px] mb-2">
            <span className="font-semibold" style={{ color:T.mid }}>Subscription progress</span>
            <span className="font-bold" style={{ color:T.olive }}>{usedPct}% used</span>
          </div>
          <div className="h-[5px] rounded-full overflow-hidden" style={{ background:"rgba(239,229,216,0.6)" }}>
            <motion.div className="h-full rounded-full" initial={{ width:0 }} animate={{ width:`${usedPct}%` }}
              transition={{ duration:1, ease:"easeOut" }}
              style={{ background:`linear-gradient(90deg,${T.olive},${T.olive2})` }}/>
          </div>
          <div className="flex justify-between text-[10px] mt-1.5" style={{ color:T.light }}>
            <span>{sub.start}</span><span>{sub.end}</span>
          </div>
        </div>

        {/* Timeline */}
        <div className="px-6 py-4 border-b" style={{ borderColor:"rgba(239,229,216,0.5)" }}>
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3" style={{ color:T.light }}>Activity Timeline</p>
          {timeline.map((t, i) => (
            <div key={i} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-0.5" style={{ background:t.color }}/>
                {i < timeline.length - 1 && <div className="w-px flex-shrink-0" style={{ height:28, background:"rgba(239,229,216,0.7)", marginLeft:"1px" }}/>}
              </div>
              <div className={i < timeline.length - 1 ? "pb-2.5" : ""}>
                <p className="text-[13px] font-semibold text-[#1F1F1F]">{t.label}</p>
                <p className="text-[11px]" style={{ color:T.light }}>{t.date}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Admin Actions */}
        <div className="px-6 py-5">
          <p className="text-[9px] font-black uppercase tracking-[1.2px] mb-3" style={{ color:T.light }}>Admin Actions</p>
          <div className="flex flex-col gap-2.5">
            <motion.button whileHover={{ y:-1, scale:1.01 }} whileTap={{ scale:0.98 }}
              onClick={() => { onAction("extend", sub); onClose(); }}
              className="w-full h-11 rounded-full text-white font-extrabold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer shadow-md"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow:`0 6px 18px rgba(107,138,90,0.38)`, fontFamily:"'Inter',sans-serif" }}>
              <FiCalendar size={14}/> Extend Subscription
            </motion.button>

            <motion.button whileHover={{ y:-1, scale:1.01 }} whileTap={{ scale:0.98 }}
              onClick={() => { onAction("upgrade", sub); onClose(); }}
              className="w-full h-11 rounded-full text-white font-extrabold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer shadow-md"
              style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow:`0 6px 18px rgba(242,154,12,0.38)`, fontFamily:"'Inter',sans-serif" }}>
              <FiArrowUp size={14}/> Upgrade / Change Plan
            </motion.button>

            <div className="grid grid-cols-2 gap-2.5">
              <motion.button whileHover={{ y:-1 }} onClick={() => { onAction("invoice", sub); onClose(); }}
                className="h-10 rounded-full text-[12px] font-bold flex items-center justify-center gap-1.5 cursor-pointer bg-white border border-amber-950/20 text-zinc-800 shadow-2xs hover:bg-amber-50">
                <FiFileText size={13} className="text-amber-600"/> Invoice
              </motion.button>

              <motion.button whileHover={{ y:-1 }} onClick={() => { onAction("refund", sub); onClose(); }}
                className="h-10 rounded-full text-[12px] font-bold flex items-center justify-center gap-1.5 cursor-pointer bg-white border border-amber-950/20 text-zinc-800 shadow-2xs hover:bg-red-50">
                <FiRefreshCw size={13} className="text-red-500"/> Refund
              </motion.button>
            </div>

            <motion.button whileHover={{ background:"rgba(226,75,74,0.14)" }}
              onClick={() => { onAction("cancel", sub); onClose(); }}
              className="w-full h-10 rounded-full text-[12px] font-extrabold flex items-center justify-center gap-2 cursor-pointer border"
              style={{ background:"rgba(226,75,74,0.08)", color:T.red, borderColor:"rgba(226,75,74,0.2)", fontFamily:"'Inter',sans-serif", transition:"background 0.2s" }}>
              <FiX size={13}/> Cancel Subscription
            </motion.button>
          </div>
        </div>
      </motion.div>
    </>,
    document.body
  );
}

// ─── 5 INTERACTIVE ACTION MODALS ──────────────────────────────────────────────

function ExtendSubModal({ sub, onClose, onSuccess, showToast }) {
  const [mode, setMode] = useState("add"); // "add" | "reduce"
  const [days, setDays] = useState(null); // No default option pre-selected
  const [customDays, setCustomDays] = useState("");
  const [customErr, setCustomErr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const targetUser = sub?.name || sub?.fullName || sub?.userName || sub?.email || "User";

  const maxReduceDays = useMemo(() => {
    if (!sub?.end || sub.end === "Unlimited") return 0;
    const endDate = new Date(sub.end);
    if (isNaN(endDate.getTime())) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const exp = new Date(endDate);
    exp.setHours(0, 0, 0, 0);
    const diffMs = exp.getTime() - today.getTime();
    return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  }, [sub?.end]);

  const rawNumDays = customDays ? Number(customDays) : days;
  const numDays = rawNumDays ? Math.abs(Number(rawNumDays)) : 0;
  const effectiveDays = mode === "reduce" ? -numDays : numDays;

  const currentEndDate = (() => {
    if (sub?.end && sub.end !== "Unlimited") {
      const d = new Date(sub.end);
      if (!isNaN(d.getTime())) return d;
    }
    return new Date();
  })();

  const newEndDate = numDays > 0 ? new Date(currentEndDate.getTime() + effectiveDays * 24 * 60 * 60 * 1000) : null;
  const newEndDateStr = newEndDate ? newEndDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "Select preset or enter custom days";

  const handleCustomDaysChange = (val, currentMode = mode) => {
    setCustomDays(val);
    if (!val) {
      if (currentMode === "reduce") {
        if (maxReduceDays <= 0) {
          setCustomErr(`Subscription has already expired or expires today (${sub?.end || "today"}). Days cannot be reduced.`);
        } else {
          setCustomErr("");
        }
      } else {
        setCustomErr("");
      }
      return;
    }

    if (currentMode === "add") {
      if (val.includes("-") || Number(val) < 0) {
        setCustomErr("Minus sign (-) is not allowed in Add Days mode. Enter a positive number.");
        return;
      }
    } else {
      if (val.includes("+") || val.includes("-") || Number(val) <= 0) {
        setCustomErr("Signs (+/-) are not allowed in Reduce Days mode. Enter a positive number of days to reduce.");
        return;
      }
      const n = Number(val);
      if (maxReduceDays <= 0) {
        setCustomErr(`Subscription has already expired or expires today (${sub?.end || "today"}). Days cannot be reduced.`);
        return;
      }
      if (n > maxReduceDays) {
        setCustomErr(`Cannot reduce by ${n} days. Maximum reduction allowed for this account is ${maxReduceDays} day(s) to expire today.`);
        return;
      }
    }

    const n = Number(val);
    if (isNaN(n) || n <= 0) {
      setCustomErr("Please enter a valid positive number of days (greater than 0).");
    } else {
      setCustomErr("");
    }
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === "reduce") {
      if (maxReduceDays <= 0) {
        setCustomErr(`Subscription has already expired or expires today (${sub?.end || "today"}). Days cannot be reduced.`);
      } else if (days > maxReduceDays && !customDays) {
        const validPreset = [7, 15, 30, 45, 60, 90].find(d => d <= maxReduceDays) || maxReduceDays;
        setDays(validPreset);
        setCustomErr("");
      } else {
        setCustomErr("");
      }
    } else {
      setCustomErr("");
    }
    if (customDays) {
      handleCustomDaysChange(customDays, newMode);
    }
  };

  const handleExtend = async () => {
    if (mode === "reduce") {
      if (maxReduceDays <= 0) {
        showToast(`Subscription has already expired or expires today. Days cannot be reduced.`, "error");
        return;
      }
      if (numDays > maxReduceDays) {
        showToast(`Cannot reduce by ${numDays} days. Maximum reduction allowed for this account is ${maxReduceDays} day(s) to expire today.`, "error");
        return;
      }
    }
    if (customErr) {
      showToast(customErr, "error");
      return;
    }
    if (!numDays || numDays <= 0) {
      showToast("Please select or enter a valid positive number of days.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await adminApi.extendSubscription({
        subscriptionId: (sub?.id && Number(sub.id) < 90000) ? Number(sub.id) : null,
        userId: Number(sub?.userId || sub?.id),
        days: effectiveDays,
      });

      sendSubscriptionNotification({
        userId: sub?.userId || sub?.id,
        title: mode === "add" ? "Subscription Extended" : "Subscription Duration Adjusted",
        message: mode === "add" 
          ? `Your subscription has been extended by +${numDays} extra days until ${newEndDateStr}.`
          : `Your subscription duration was adjusted by -${numDays} days until ${newEndDateStr}.`
      });

      showToast(res.message || `Duration adjusted successfully! (${mode === "add" ? "+" : "-"}${numDays}d)`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || "Failed to adjust subscription duration.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background:"rgba(15,15,15,0.55)", backdropFilter:"blur(8px)" }} onClick={onClose}>
      <motion.div initial={{ scale:0.95, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.95, opacity:0 }}
        className="relative rounded-[28px] p-7 max-w-[480px] w-full my-auto"
        style={{ background:"#FFFFFF", boxShadow:"0 30px 90px rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.9)" }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center text-white shadow-md ${mode === "add" ? "bg-emerald-600" : "bg-rose-600"}`}>
              <FiCalendar size={18}/>
            </div>
            <div>
              <h3 className="font-black text-[16px] text-zinc-900 tracking-tight">Adjust Subscription Duration</h3>
              <p className="text-[11px] text-zinc-500 font-medium">Increment or decrement days for <strong className="text-zinc-900">{targetUser}</strong></p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 border-none cursor-pointer">
            <FiX size={15}/>
          </button>
        </div>

        {/* Mode Switcher: Add (+) vs Reduce (-) */}
        <div className="grid grid-cols-2 gap-2 p-1.5 rounded-2xl bg-zinc-100 mb-4">
          <button type="button" onClick={() => handleModeChange("add")}
            className={`py-2 rounded-xl text-[12px] font-extrabold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
              mode === "add" ? "bg-emerald-600 text-white shadow-md" : "text-zinc-600 hover:text-zinc-900"
            }`}>
            <span>+</span> Add Days (Increment)
          </button>
          <button type="button" onClick={() => handleModeChange("reduce")}
            className={`py-2 rounded-xl text-[12px] font-extrabold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
              mode === "reduce" ? "bg-rose-600 text-white shadow-md" : "text-zinc-600 hover:text-zinc-900"
            }`}>
            <span>-</span> Reduce Days (Decrement)
          </button>
        </div>

        {mode === "reduce" && maxReduceDays <= 0 && (
          <div className="p-3.5 rounded-2xl bg-amber-50 border border-amber-200/90 text-amber-900 mb-4 flex items-start gap-2.5 text-[11.5px] font-medium leading-relaxed">
            <FiAlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              <strong className="font-bold block text-amber-950">Days Cannot Be Reduced</strong>
              This subscription has already expired or expires today ({sub?.end || "today"}). Reduction is disabled. Please switch to <strong>Add Days (+)</strong> or <strong>Renew</strong>.
            </div>
          </div>
        )}

        <div className="space-y-4 my-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-zinc-500 block mb-2">
              Select Preset ({mode === "add" ? "Add" : "Reduce"})
            </label>
            <div className="grid grid-cols-4 gap-2">
              {(mode === "add" ? [7, 15, 30, 60, 90, 180, 365] : [7, 15, 30, 45, 60, 90]).map(d => {
                const isDisabledPreset = mode === "reduce" && (maxReduceDays <= 0 || d > maxReduceDays);
                return (
                  <button key={d} type="button"
                    disabled={isDisabledPreset}
                    onClick={() => { setDays(d); setCustomDays(""); setCustomErr(""); }}
                    className={`py-2 rounded-xl text-[12px] font-black border transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed ${
                      days === d && !customDays && !isDisabledPreset
                        ? (mode === "add" ? "bg-emerald-500 text-white border-emerald-500 shadow-md" : "bg-rose-500 text-white border-rose-500 shadow-md") 
                        : "bg-zinc-50 text-zinc-700 border-zinc-200 hover:bg-zinc-100"
                    }`}>
                    {mode === "add" ? `+${d}` : `-${d}`} Days
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] font-black uppercase tracking-wider text-zinc-500 block">Or Enter Custom Days</label>
              <span className="text-[10px] font-bold text-zinc-400">
                {mode === "reduce" && maxReduceDays > 0 ? `Max ${maxReduceDays} day(s)` : "Positive numbers only"}
              </span>
            </div>
            <input type="number" min="1" step="1"
              disabled={mode === "reduce" && maxReduceDays <= 0}
              value={customDays} onChange={e => handleCustomDaysChange(e.target.value)} placeholder={mode === "add" ? "e.g. 90" : `e.g. ${Math.min(9, maxReduceDays || 9)}`}
              className="w-full rounded-xl px-3.5 py-2.5 text-[13px] font-bold outline-none transition-all border disabled:bg-zinc-100 disabled:cursor-not-allowed"
              style={{
                borderColor: customErr ? "#EF4444" : "rgba(228,228,231,1)",
                background: customErr ? "rgba(254,242,242,0.6)" : "#FFFFFF"
              }}
            />
            {customErr && (
              <p className="text-[11px] font-bold text-red-500 flex items-center gap-1 mt-1.5">
                <FiAlertTriangle size={11} className="shrink-0" /> {customErr}
              </p>
            )}
          </div>

          <div className={`p-3.5 rounded-2xl border flex flex-col gap-1 ${mode === "add" ? "bg-emerald-50/70 border-emerald-200" : "bg-rose-50/70 border-rose-200"}`}>
            <div className="flex justify-between text-[11px] text-zinc-600 font-semibold">
              <span>Current Expiry:</span>
              <span className="font-bold text-zinc-900">{sub?.end || "Today"}</span>
            </div>
            <div className="flex justify-between text-[12px] font-black pt-1 border-t border-zinc-200/60">
              <span className={mode === "add" ? "text-emerald-900" : "text-rose-900"}>Adjusted New Expiry:</span>
              <span className={mode === "add" ? "text-emerald-600" : "text-rose-600"}>{newEndDateStr}</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2.5 mt-5">
          <button disabled={submitting || Boolean(customErr) || (mode === "reduce" && (maxReduceDays <= 0 || numDays > maxReduceDays))} onClick={handleExtend}
            className={`flex-1 py-3 rounded-full text-white text-[13px] font-extrabold border-none cursor-pointer shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
              mode === "add" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"
            }`}>
            {submitting ? "Saving..." : `Confirm ${mode === "add" ? "Extension" : "Reduction"} (${mode === "add" ? "+" : "-"}${numDays}d)`}
          </button>
          <button onClick={onClose} className="px-5 py-3 rounded-full text-[13px] font-bold bg-zinc-100 text-zinc-700 border border-zinc-200 cursor-pointer">
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

function UpgradeSubModal({ sub, packages = [], onClose, onSuccess, showToast }) {
  const activePackages = packages.filter(p => p.isActive !== false);
  const [selectedPkgId, setSelectedPkgId] = useState(""); // No default option pre-selected
  const [submitting, setSubmitting] = useState(false);

  // Prevent background UI page scrolling while modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const selectedPkg = activePackages.find(p => p.id === Number(selectedPkgId)) || null;
  const pkgPrice = selectedPkg ? (selectedPkg.discountedPrice || selectedPkg.price || 0) : 0;

  const handleUpgrade = async () => {
    if (!selectedPkgId) { showToast("Please select a target plan."); return; }
    const rawUserId = sub?.userId ?? sub?.id;
    const targetUserId = Number(rawUserId) > 90000 ? Number(rawUserId) - 90000 : Number(rawUserId);

    if (!targetUserId || isNaN(targetUserId)) {
      showToast("Unable to identify target user.", "error");
      return;
    }

    setSubmitting(true);
    try {
      const res = await adminApi.createSubscription({
        userId: targetUserId,
        packageId: Number(selectedPkgId),
        startDate: new Date().toISOString(),
        amountPaid: Number(pkgPrice),
        paymentStatus: "Completed",
      });

      sendSubscriptionNotification({
        userId: targetUserId,
        title: "Plan Tier Upgraded",
        message: `Your subscription plan has been upgraded to ${selectedPkg?.name || "New Plan"}. Enjoy full live access!`
      });

      packageService.notifyPackageChange();
      window.dispatchEvent(new Event("subscription-updated"));

      showToast(res?.message || `Plan changed successfully to ${selectedPkg?.name || "New Plan"}!`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || "Failed to upgrade plan.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background:"rgba(15,15,15,0.55)", backdropFilter:"blur(8px)", overscrollBehavior:"contain" }} onClick={onClose}>
      <motion.div initial={{ scale:0.95, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.95, opacity:0 }}
        className="relative rounded-[28px] p-7 max-w-[520px] w-full my-auto"
        style={{ background:"#FFFFFF", boxShadow:"0 30px 90px rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.9)", overscrollBehavior:"contain" }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white bg-amber-500 shadow-md">
              <FiArrowUp size={18}/>
            </div>
            <div>
              <h3 className="font-black text-[16px] text-zinc-900 tracking-tight">Upgrade / Change Plan</h3>
              <p className="text-[11px] text-zinc-500 font-medium">Reassign plan tier for {sub?.name || sub?.fullName || sub?.userName || sub?.email || "User"}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 border-none cursor-pointer">
            <FiX size={15}/>
          </button>
        </div>

        <div
          onWheel={(e) => e.stopPropagation()}
          className="space-y-3 my-4 max-h-[320px] overflow-y-auto pr-1.5 [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth:"none", msOverflowStyle:"none", touchAction:"pan-y", overscrollBehavior:"contain" }}>
          {activePackages.map(p => {
            const isSelected = Number(selectedPkgId) === p.id;
            return (
              <div key={p.id} onClick={() => setSelectedPkgId(p.id)}
                className={`p-3.5 rounded-2xl border transition-all cursor-pointer flex items-center justify-between ${
                  isSelected ? "border-amber-500 bg-amber-50/70 shadow-sm ring-1 ring-amber-500" : "border-zinc-200 bg-white hover:bg-zinc-50"
                }`}>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-extrabold text-[13px] text-zinc-900">{p.name}</span>
                    <TierPill tier={p.name}/>
                  </div>
                  <p className="text-[11px] text-zinc-500">{p.durationInDays} Days Duration • Full Live Access</p>
                </div>
                <div className="text-right">
                  <span className="font-black text-[14px] text-amber-700">₹{(p.discountedPrice || p.price).toLocaleString()}</span>
                  {isSelected && <FiCheckCircle size={16} className="text-amber-600 ml-auto mt-1"/>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-2.5 mt-5">
          <button disabled={submitting || !selectedPkgId} onClick={handleUpgrade}
            className="flex-1 py-3 rounded-full text-white text-[13px] font-extrabold bg-amber-500 hover:bg-amber-600 border-none cursor-pointer shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? "Updating Plan..." : selectedPkg ? `Confirm Plan Change to ${selectedPkg.name}` : "Select a Target Plan"}
          </button>
          <button onClick={onClose} className="px-5 py-3 rounded-full text-[13px] font-bold bg-zinc-100 text-zinc-700 border border-zinc-200 cursor-pointer">
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

function InvoiceModal({ sub, onClose }) {
  const invNo = `INV-2026-${String(sub.id || Math.floor(Math.random()*90000+10000)).padStart(5,"0")}`;
  const invDate = sub.start || new Date().toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"numeric" });
  const paidAmt = sub.amountPaid ?? sub.amount ?? 0;
  const baseAmt = Math.round(paidAmt / 1.18);
  const gstAmt = paidAmt - baseAmt;

  return createPortal(
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background:"rgba(15,15,15,0.55)", backdropFilter:"blur(8px)" }} onClick={onClose}>
      <motion.div initial={{ scale:0.95, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.95, opacity:0 }}
        className="relative rounded-[28px] p-8 max-w-[560px] w-full my-auto"
        style={{ background:"#FFFFFF", boxShadow:"0 40px 100px rgba(0,0,0,0.3)" }}
        onClick={e => e.stopPropagation()}>
        
        {/* Printable Area */}
        <div id="printable-invoice">
          {/* Header Branding */}
          <div className="flex items-center justify-between pb-5 border-b border-zinc-200">
            <div>
              <h2 className="font-black text-[22px] text-amber-600 tracking-tight" style={{ fontFamily:"'Poppins',sans-serif" }}>Divyyog</h2>
              <p className="text-[11px] text-zinc-500 font-semibold">Premier Wellness & Yoga Institute</p>
            </div>
            <div className="text-right">
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-black uppercase tracking-wider">
                PAID INVOICE
              </span>
              <p className="text-[12px] font-extrabold text-zinc-900 mt-1">{invNo}</p>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4 my-5 text-[12px]">
            <div>
              <p className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mb-1">Billed To</p>
              <p className="font-extrabold text-zinc-900 text-[14px]">{sub.name || "Customer"}</p>
              <p className="text-zinc-600">{sub.email || "No email"}</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mb-1">Invoice Details</p>
              <p className="text-zinc-700"><span className="font-bold">Date:</span> {invDate}</p>
              <p className="text-zinc-700"><span className="font-bold">Payment Method:</span> {sub.method}</p>
              <p className="text-zinc-700 font-mono text-[11px]"><span className="font-bold">TxID:</span> {sub.txId}</p>
            </div>
          </div>

          {/* Itemized Table */}
          <div className="rounded-2xl border border-zinc-200 overflow-hidden mb-6">
            <table className="w-full text-[12px] text-left">
              <thead className="bg-zinc-50 text-zinc-600 font-extrabold border-b border-zinc-200">
                <tr>
                  <th className="p-3">Description</th>
                  <th className="p-3 text-right">Tax Rate</th>
                  <th className="p-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 text-zinc-800 font-medium">
                <tr>
                  <td className="p-3">
                    <span className="font-bold block text-zinc-900">{sub.tier} Subscription Package</span>
                    <span className="text-[10px] text-zinc-500">Access to live classes, library & trainer guidance</span>
                  </td>
                  <td className="p-3 text-right">18% GST</td>
                  <td className="p-3 text-right font-bold">₹{baseAmt.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="p-3 text-zinc-500 font-semibold" colSpan={2}>CGST + SGST (18%)</td>
                  <td className="p-3 text-right font-bold">₹{gstAmt.toLocaleString()}</td>
                </tr>
              </tbody>
              <tfoot className="bg-amber-50/80 font-black text-[#1F1F1F] border-t border-amber-200">
                <tr>
                  <td className="p-3 text-[13px]" colSpan={2}>Total Amount Paid</td>
                  <td className="p-3 text-right text-[15px] text-amber-700">₹{paidAmt.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex gap-2.5">
          <button onClick={() => window.print()}
            className="flex-1 py-3 rounded-full text-white text-[13px] font-extrabold bg-zinc-900 hover:bg-black border-none cursor-pointer flex items-center justify-center gap-2 shadow-md">
            <FiDownload size={15}/> Print / Download PDF
          </button>
          <button onClick={onClose} className="px-6 py-3 rounded-full text-[13px] font-bold bg-zinc-100 text-zinc-700 border border-zinc-200 cursor-pointer">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

function RefundSubModal({ sub, onClose, onSuccess, showToast }) {
  const [reason, setReason] = useState("Customer requested cancellation");
  const [submitting, setSubmitting] = useState(false);

  const handleRefund = async () => {
    setSubmitting(true);
    try {
      await adminApi.cancelSubscription(sub.id);

      sendSubscriptionNotification({
        userId: sub.userId,
        title: "Subscription Refund Processed",
        message: `A refund of ₹${(sub.amountPaid ?? sub.amount ?? 0).toLocaleString()} has been processed for your subscription.`
      });

      showToast(`Refund processed for ${sub.name}! ₹${(sub.amountPaid ?? sub.amount ?? 0).toLocaleString()} credited back.`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast(err.message || "Failed to process refund.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background:"rgba(15,15,15,0.55)", backdropFilter:"blur(8px)" }} onClick={onClose}>
      <motion.div initial={{ scale:0.95, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.95, opacity:0 }}
        className="relative rounded-[28px] p-7 max-w-[460px] w-full my-auto"
        style={{ background:"#FFFFFF", boxShadow:"0 30px 90px rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.9)" }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white bg-red-500 shadow-md">
              <FiRefreshCw size={18}/>
            </div>
            <div>
              <h3 className="font-black text-[16px] text-zinc-900 tracking-tight">Process Refund</h3>
              <p className="text-[11px] text-zinc-500 font-medium">Issue refund to {sub.name || "User"}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 border-none cursor-pointer">
            <FiX size={15}/>
          </button>
        </div>

        <div className="p-4 rounded-2xl bg-red-50 border border-red-200/80 my-4 space-y-2">
          <div className="flex justify-between text-[12px] font-bold text-zinc-700">
            <span>Refund Amount:</span>
            <span className="font-black text-red-600 text-[14px]">₹{(sub.amountPaid ?? sub.amount ?? 0).toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-[11px] text-zinc-500">
            <span>Transaction ID:</span>
            <span className="font-mono text-zinc-900">{sub.txId}</span>
          </div>
        </div>

        <div className="mb-5">
          <label className="text-[10px] font-black uppercase tracking-wider text-zinc-500 block mb-1.5">Refund Reason</label>
          <CustomDropdown
            value={reason}
            onChange={setReason}
            options={[
              "Customer requested cancellation",
              "Accidental duplicate payment",
              "Medical reason / Health pause",
              "Unsatisfactory service"
            ]}
            label="Refund Reason"
            icon={FiRefreshCw}
          />
        </div>

        <div className="flex gap-2.5">
          <button disabled={submitting} onClick={handleRefund}
            className="flex-1 py-3 rounded-full text-white text-[13px] font-extrabold bg-red-600 hover:bg-red-700 border-none cursor-pointer shadow-lg disabled:opacity-50">
            {submitting ? "Processing..." : "Confirm & Issue Refund"}
          </button>
          <button onClick={onClose} className="px-5 py-3 rounded-full text-[13px] font-bold bg-zinc-100 text-zinc-700 border border-zinc-200 cursor-pointer">
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

function CancelSubModal({ sub, onClose, onSuccess, showToast }) {
  const [submitting, setSubmitting] = useState(false);

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      await adminApi.cancelSubscription(sub.id);

      sendSubscriptionNotification({
        userId: sub.userId,
        title: "Subscription Cancelled",
        message: `Your active ${sub.tier || 'subscription'} plan has been cancelled.`
      });

      showToast(`Subscription cancelled for ${sub.name || "User"}.`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast(err.message || "Failed to cancel subscription.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background:"rgba(15,15,15,0.55)", backdropFilter:"blur(8px)" }} onClick={onClose}>
      <motion.div initial={{ scale:0.95, opacity:0 }} animate={{ scale:1, opacity:1 }} exit={{ scale:0.95, opacity:0 }}
        className="relative rounded-[28px] p-7 max-w-[440px] w-full my-auto"
        style={{ background:"#FFFFFF", boxShadow:"0 30px 90px rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.9)" }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white bg-red-600 shadow-md">
              <FiAlertTriangle size={18}/>
            </div>
            <div>
              <h3 className="font-black text-[16px] text-zinc-900 tracking-tight">Cancel Subscription</h3>
              <p className="text-[11px] text-zinc-500 font-medium">Terminate plan for {sub.name || "User"}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 border-none cursor-pointer">
            <FiX size={15}/>
          </button>
        </div>

        <p className="text-[13px] text-zinc-600 leading-relaxed my-4 font-medium">
          Are you sure you want to cancel the active <strong className="text-zinc-900">{sub.tier}</strong> subscription for <strong className="text-zinc-900">{sub.name}</strong>? This action will set the status to Cancelled.
        </p>

        <div className="flex gap-2.5">
          <button disabled={submitting} onClick={handleCancel}
            className="flex-1 py-3 rounded-full text-white text-[13px] font-extrabold bg-red-600 hover:bg-red-700 border-none cursor-pointer shadow-lg disabled:opacity-50">
            {submitting ? "Cancelling..." : "Yes, Cancel Subscription"}
          </button>
          <button onClick={onClose} className="px-5 py-3 rounded-full text-[13px] font-bold bg-zinc-100 text-zinc-700 border border-zinc-200 cursor-pointer">
            Keep Active
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

// ─── USER SEARCH COMBOBOX (LIVE SEARCH BY NAME & EMAIL) ──────────────────────
function UserSearchCombobox({ users = [], selectedUserId, onSelectUser, userSearch, setUserSearch }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedUser = users.find(u => String(u.id) === String(selectedUserId));

  // Local filtering by Name OR Email:
  const filteredUsers = useMemo(() => {
    if (!userSearch || userSearch.trim() === "") return users;
    if (selectedUser && (selectedUser.fullName === userSearch || `${selectedUser.fullName} (${selectedUser.email})` === userSearch)) {
      return users;
    }
    const q = userSearch.toLowerCase().trim();
    return users.filter(u => 
      (u.fullName || "").toLowerCase().includes(q) || 
      (u.email || "").toLowerCase().includes(q)
    );
  }, [users, userSearch, selectedUser]);

  return (
    <div className="relative w-full" ref={containerRef}>
      <div className="relative flex items-center">
        <FiSearch size={15} className="absolute left-3.5 text-amber-500 pointer-events-none z-10"/>
        <input
          type="text"
          value={userSearch}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setUserSearch(e.target.value);
            setOpen(true);
          }}
          placeholder="Type to search user by name or email…"
          className="w-full h-11 pl-10 pr-9 rounded-[14px] text-[13px] font-bold outline-none border transition-all bg-white text-zinc-900"
          style={{
            borderColor: open ? "#F29A0C" : "rgba(239,229,216,0.95)",
            boxShadow: open ? "0 0 0 3.5px rgba(242,154,12,0.18), 0 8px 24px rgba(0,0,0,0.06)" : "0 2px 8px rgba(0,0,0,0.03)"
          }}
        />
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="absolute right-3 text-zinc-400 hover:text-amber-600 border-none bg-transparent cursor-pointer">
          <FiChevronDown size={15} className={`transition-transform duration-200 ${open ? "rotate-180 text-amber-600" : ""}`}/>
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute left-0 right-0 top-[48px] z-[260] p-1.5 rounded-[18px] border overflow-hidden bg-white shadow-2xl max-h-[230px] overflow-y-auto [&::-webkit-scrollbar]:hidden"
            style={{
              borderColor: "rgba(239,229,216,0.95)",
              boxShadow: "0 20px 50px rgba(0,0,0,0.16)",
              scrollbarWidth: "none",
              msOverflowStyle: "none"
            }}>
            {filteredUsers.length === 0 ? (
              <div className="py-4 text-center text-[12px] text-zinc-400 font-semibold">
                No users found matching "{userSearch}"
              </div>
            ) : (
              filteredUsers.map(u => {
                const isSelected = String(u.id) === String(selectedUserId);
                const avatarUrl = u.profileImageUrl || u.avatarUrl;
                const initials = (u.fullName || u.email || "U").substring(0, 2).toUpperCase();

                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => {
                      onSelectUser(u.id);
                      setUserSearch(`${u.fullName || 'User'} (${u.email})`);
                      setOpen(false);
                    }}
                    className={`w-full text-left p-2.5 rounded-[12px] flex items-center justify-between cursor-pointer transition-all border-none my-0.5 ${
                      isSelected 
                        ? "bg-amber-500 text-white shadow-sm" 
                        : "hover:bg-amber-50/80 text-zinc-800"
                    }`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="" className="w-8 h-8 rounded-full object-cover shrink-0 border border-amber-900/10"/>
                      ) : (
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-[11px] shrink-0 ${
                          isSelected ? "bg-white text-amber-700" : "bg-gradient-to-br from-amber-400 to-orange-500 text-white"
                        }`}>
                          {initials}
                        </div>
                      )}

                      <div className="min-w-0">
                        <p className={`text-[13px] font-extrabold truncate leading-tight ${isSelected ? "text-white" : "text-zinc-900"}`}>
                          {u.fullName || "User"}
                        </p>
                        <p className={`text-[11px] font-medium truncate ${isSelected ? "text-amber-100" : "text-zinc-500"}`}>
                          {u.email}
                        </p>
                      </div>
                    </div>

                    {isSelected && <FiCheckCircle size={15} className="text-white shrink-0 ml-2"/>}
                  </button>
                );
              })
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── ACTIVATION MODAL (MANUAL USER ASSIGNMENT) ──────────────────────────────
function ActivateModal({ packages = [], onClose, showToast, onSuccess }) {
  const [internalPackages, setInternalPackages] = useState(packages || []);
  const activePackages = useMemo(() => (internalPackages || []).filter(p => p.isActive !== false), [internalPackages]);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedPkgId, setSelectedPkgId] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (packages && packages.length > 0) {
      setInternalPackages(packages);
    } else {
      adminApi.getPackages().then(res => {
        if (res?.data) setInternalPackages(res.data);
      }).catch(() => null);
    }
  }, [packages]);

  useEffect(() => {
    adminApi.getUsers({ search: "", pageSize: 100 })
      .then(res => {
        const uList = res.data?.users || res.data || [];
        setUsers(uList);
      })
      .catch(() => null);
  }, []);

  const packageDropdownOptions = useMemo(() => [
    { label: "Select Subscription Package", value: "" },
    ...activePackages.map(p => {
      const displayPrice = p.discountedPrice !== undefined && p.discountedPrice !== null ? p.discountedPrice : p.price;
      return {
        label: `${p.name} (${p.durationInDays || 30} days · ₹${Number(displayPrice || 0).toLocaleString()})`,
        value: String(p.id)
      };
    })
  ], [activePackages]);

  const selectedUserObj = useMemo(() => {
    return users.find(u => String(u.id) === String(selectedUserId));
  }, [users, selectedUserId]);

  const selectedPkgObj = useMemo(() => {
    return activePackages.find(p => String(p.id) === String(selectedPkgId));
  }, [activePackages, selectedPkgId]);

  const validateField = (field, value) => {
    let err = "";
    if (field === "userId") {
      if (!value) err = "Please search and select a user account.";
    } else if (field === "packageId") {
      if (!value) err = "Please select a subscription package.";
    } else if (field === "amountPaid") {
      if (value === "" || value === null || value === undefined) {
        err = "Amount paid is required.";
      } else {
        const num = Number(value);
        if (isNaN(num)) {
          err = "Please enter a valid numeric amount.";
        } else if (num < 0) {
          err = "Amount paid cannot be negative (must be ₹0 or greater).";
        }
      }
    }
    return err;
  };

  const handleUserSelect = (uId) => {
    setSelectedUserId(uId);
    setTouched(prev => ({ ...prev, userId: true }));
    const err = validateField("userId", uId);
    setErrors(prev => ({ ...prev, userId: err }));
  };

  const handlePkgChange = (pkgId) => {
    setSelectedPkgId(pkgId);
    setTouched(prev => ({ ...prev, packageId: true }));
    const pkgErr = validateField("packageId", pkgId);
    setErrors(prev => ({ ...prev, packageId: pkgErr }));

    if (pkgId) {
      const p = activePackages.find(pkg => String(pkg.id) === String(pkgId));
      if (p) {
        const finalPrice = p.discountedPrice !== undefined && p.discountedPrice !== null ? p.discountedPrice : p.price;
        const priceStr = String(finalPrice ?? "");
        setAmountPaid(priceStr);
        setTouched(prev => ({ ...prev, amountPaid: true }));
        setErrors(prev => ({ ...prev, amountPaid: validateField("amountPaid", priceStr) }));
      }
    } else {
      setAmountPaid("");
    }
  };

  const handleAmountChange = (val) => {
    setAmountPaid(val);
    setTouched(prev => ({ ...prev, amountPaid: true }));
    const err = validateField("amountPaid", val);
    setErrors(prev => ({ ...prev, amountPaid: err }));
  };

  const validateAll = () => {
    const newErrors = {};
    const uErr = validateField("userId", selectedUserId);
    if (uErr) newErrors.userId = uErr;

    const pErr = validateField("packageId", selectedPkgId);
    if (pErr) newErrors.packageId = pErr;

    const aErr = validateField("amountPaid", amountPaid);
    if (aErr) newErrors.amountPaid = aErr;

    setTouched({ userId: true, packageId: true, amountPaid: true });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (!validateAll()) {
      showToast("Please correct the highlighted validation errors before activating.", "error");
      return;
    }

    setSubmitting(true);
    try {
      await adminApi.createSubscription({
        userId: Number(selectedUserId),
        packageId: Number(selectedPkgId),
        startDate: new Date().toISOString(),
        amountPaid: Number(amountPaid),
        paymentStatus: "Completed",
      });

      sendSubscriptionNotification({
        userId: selectedUserId,
        title: "Subscription Plan Activated",
        message: `Your subscription plan (${selectedPkgObj?.name || 'Plan'}) has been successfully activated.`
      });

      showToast("✓ Subscription activated! User granted instant active access.");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast(err.message || "Failed to activate subscription.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background:"rgba(15,15,15,0.55)", backdropFilter:"blur(8px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.95, opacity:0 }} animate={{ scale:1, opacity:1 }}
        exit={{ scale:0.95, opacity:0 }} transition={{ duration:0.28, ease:[0.25,1,0.5,1] }}
        className="relative overflow-visible rounded-[30px] p-7 sm:p-8 max-w-[520px] w-full my-auto"
        style={{ background:"rgba(255,253,248,0.98)", boxShadow:"0 40px 100px rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.95)" }}
        onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[13px] flex items-center justify-center text-white"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow:`0 5px 14px rgba(107,138,90,0.4)` }}>
              <FiCheck size={18}/>
            </div>
            <div>
              <p className="font-black text-[17px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>Manual User Subscription</p>
              <p className="text-[11px] text-zinc-400">Assign and activate a plan directly to any user</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none bg-zinc-200/60 text-zinc-600 cursor-pointer hover:bg-zinc-300 transition-colors">
            <FiX size={15}/>
          </button>
        </div>

        <div className="space-y-4 mb-5">
          {/* User Live Search Combobox */}
          <div>
            <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>
              Search & Select User <span className="text-red-500">*</span>
            </label>
            <UserSearchCombobox
              users={users}
              selectedUserId={selectedUserId}
              onSelectUser={handleUserSelect}
              userSearch={userSearch}
              setUserSearch={(str) => {
                setUserSearch(str);
                if (!str) handleUserSelect("");
              }}
            />
            {touched.userId && errors.userId ? (
              <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.userId}
              </p>
            ) : (
              <p className="text-[10px] text-zinc-400 mt-1 font-medium">Search registered users by full name or email</p>
            )}
          </div>

          {/* Package selector */}
          <div>
            <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>
              Subscription Package <span className="text-red-500">*</span>
            </label>
            <CustomDropdown
              value={selectedPkgId}
              onChange={handlePkgChange}
              options={packageDropdownOptions}
              label="Package"
              icon={FiZap}
            />
            {touched.packageId && errors.packageId ? (
              <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.packageId}
              </p>
            ) : (
              <p className="text-[10px] text-zinc-400 mt-1 font-medium">Select package to auto-populate duration and price</p>
            )}
          </div>

          {/* Amount received */}
          <div>
            <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>
              Amount Paid (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={amountPaid}
              onChange={e => handleAmountChange(e.target.value)}
              placeholder="e.g. 1499"
              className="w-full rounded-[13px] px-3.5 py-2.5 text-[13px] font-bold outline-none border transition-all"
              style={{
                borderColor: touched.amountPaid && errors.amountPaid ? "#EF4444" : "rgba(239,229,216,0.95)",
                background: touched.amountPaid && errors.amountPaid ? "rgba(254,242,242,0.6)" : "#FFFFFF",
                color: "#1F1F1F"
              }}
            />
            {touched.amountPaid && errors.amountPaid ? (
              <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                <FiAlertTriangle size={11} className="shrink-0" /> {errors.amountPaid}
              </p>
            ) : (
              <p className="text-[10px] text-zinc-400 mt-1 font-medium">Collected payment amount in INR (must be ₹0 or greater)</p>
            )}
          </div>
        </div>

        {/* Selected Summary Card */}
        {selectedUserObj && selectedPkgObj && (
          <div className="rounded-[16px] p-3.5 mb-4 bg-amber-50 border border-amber-200/80 flex items-center justify-between">
            <div>
              <p className="text-[12px] font-bold text-amber-950">{selectedUserObj.fullName || selectedUserObj.email}</p>
              <p className="text-[11px] text-amber-700 font-medium">{selectedPkgObj.name} · {selectedPkgObj.durationInDays || 30} Days</p>
            </div>
            <div className="text-right">
              <span className="text-[15px] font-black text-amber-800">₹{Number(amountPaid || 0).toLocaleString()}</span>
            </div>
          </div>
        )}

        <div className="flex items-start gap-2.5 rounded-[14px] p-3.5 mb-5 bg-emerald-500/10 border border-emerald-500/20">
          <FiInfo size={16} className="text-emerald-700 shrink-0 mt-0.5"/>
          <p className="text-[11px] text-emerald-950 leading-relaxed font-semibold">
            Saving will instantly activate the user's subscription in DB and grant member portal access immediately.
          </p>
        </div>

        <div className="flex gap-2.5">
          <button disabled={submitting} onClick={handleConfirm}
            className="flex-1 h-11 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer shadow-lg hover:opacity-95 transition-opacity"
            style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})` }}>
            {submitting ? "Activating..." : "Confirm & Activate"}
          </button>
          <button onClick={onClose} className="h-11 px-5 rounded-full text-[13px] font-semibold border border-amber-950/20 bg-white text-zinc-700 cursor-pointer hover:bg-zinc-50 transition-colors">
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

// ─── PACKAGE CRUD MODAL ──────────────────────────────────────────────────────
function PackageModal({ pkg, existingPackages = [], onClose, showToast, onSuccess }) {
  const { tiers: dynamicTiers } = useDynamicPackages();

  const ALL_FEATURE_KEYS = useMemo(() => [
    { id: "journey_analytics", label: "My Journey Analytics", desc: "Access progress trends & wellness charts", Icon: FiActivity },
    { id: "goals_tracker", label: "Personal Goals", desc: "Access habit & goal tracking suite", Icon: FiTarget },
    { id: "attendance_logs", label: "Attendance Logs", desc: "Access class check-in history", Icon: FiCheckCircle },
    { id: "achievements", label: "Achievements & Badges", desc: "Access milestone badges & streak rewards", Icon: FiAward },
    { id: "community_forum", label: "Community Forum", desc: "Access community discussion hub", Icon: FiMessageSquare },
    { id: "customise_preferences", label: "Customise Preferences Button", desc: "Show the 'Customise Preferences' button on the Diet Plan page", Icon: FiSliders },
    { id: "ai_diet_plan", label: "AI Diet Plan Generator", desc: "Show 'Generate 7-Day Plan with AI' button inside Customise Preferences", Icon: FiZap },
  ], []);

  const getInitialAllowedFeatures = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch {}
    }
    // DEFAULT MUST BE OFF (empty array) for new packages!
    return pkg?.id ? ["journey_analytics", "goals_tracker", "attendance_logs", "achievements", "community_forum"] : [];
  };

  const [allowedFeatures, setAllowedFeatures] = useState(() => getInitialAllowedFeatures(pkg?.allowedFeatures || pkg?.allowedFeaturesJson));
  const [activeModalTab, setActiveModalTab] = useState("pricing"); // "pricing" | "permissions"

  const getInitialFeaturesText = (raw) => {
    if (!raw) return "";
    if (Array.isArray(raw)) return raw.join("\n");
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.join("\n");
      } catch {}
      return raw.split(",").map(s => s.trim()).filter(Boolean).join("\n");
    }
    return "";
  };

  const [form, setForm] = useState({
    name: pkg?.name || "",
    packageType: pkg?.packageType || "",
    accessLevel: typeof pkg?.accessLevel === "number" ? pkg.accessLevel : "",
    durationInDays: pkg?.durationInDays !== undefined && pkg?.durationInDays !== null ? String(pkg.durationInDays) : "",
    price: pkg?.price !== undefined && pkg?.price !== null ? String(pkg.price) : "",
    discountPercentage: pkg?.discountPercentage !== undefined && pkg?.discountPercentage !== null ? String(pkg.discountPercentage) : "",
    aiDietGenerationLimit: pkg?.aiDietGenerationLimit !== undefined && pkg?.aiDietGenerationLimit !== null ? String(pkg.aiDietGenerationLimit) : "5",
    badgeTag: pkg?.badgeTag || "",
    featuresText: getInitialFeaturesText(pkg?.featuresJson || pkg?.features),
    description: pkg?.description || "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Clean canonical tiers without raw alias abbreviations
  const canonicalTiers = useMemo(() => ["Free Trial", "Basic Plan", "Standard Plan", "Premium Plan", "YTT Pro"], []);
  
  const cleanDynamicTiers = useMemo(() => {
    return (dynamicTiers || []).filter(t => {
      const low = (t || "").toLowerCase().trim();
      return !["basic", "standard", "premium", "ytt", "pro", "free", "free trial", "basic plan", "standard plan", "premium plan", "ytt pro"].includes(low);
    });
  }, [dynamicTiers]);

  const tierDropdownOptions = useMemo(() => [
    { label: "Select Package Tier", value: "" },
    ...canonicalTiers.map(t => ({ label: t, value: t })),
    ...cleanDynamicTiers.map(t => ({ label: t, value: t })),
    { label: "+ Enter Custom New Tier...", value: "CUSTOM_NEW" }
  ], [canonicalTiers, cleanDynamicTiers]);

  const [isCustomTier, setIsCustomTier] = useState(() => {
    return Boolean(form.packageType && !["Basic", "Standard", "Premium", "YTT", "Pro", "Free", ...canonicalTiers, ...cleanDynamicTiers].includes(form.packageType));
  });
  const [saving, setSaving] = useState(false);

  const numPrice = Number(form.price) || 0;
  const numDisc = Number(form.discountPercentage) || 0;
  const discountedPrice = Math.max(0, Math.round(numPrice * (1 - (numDisc / 100))));

  const getPresetAccessLevel = (typeName) => {
    const low = (typeName || "").toLowerCase().trim();
    if (low === "free" || low === "free trial" || low === "trial") return 0;
    if (low.includes("basic") || low.includes("bronze")) return 1;
    if (low.includes("standard") || low.includes("silver")) return 2;
    if (low.includes("premium") || low.includes("gold")) return 3;
    if (low.includes("pro") || low.includes("ytt") || low.includes("vip")) return 4;
    return "";
  };

  const getCleanBaseStem = (str) => {
    if (!str || typeof str !== "string") return "";
    return str
      .toLowerCase()
      .trim()
      .replace(/\b(plan|tier|package|access|level)\b/g, "")
      .replace(/[^a-z0-9]/g, "")
      .trim();
  };

  const isFreeTierHelper = (typeName, nameStr) => {
    const lowType = (typeName || "").toLowerCase().trim();
    const lowName = (nameStr || "").toLowerCase().trim();
    return lowType.includes("free") || lowType.includes("trial") || lowName.includes("free") || lowName.includes("trial");
  };

  const validateField = (field, value, currentForm = form) => {
    let err = "";
    const v = typeof value === "string" ? value.trim() : value;

    if (field === "name") {
      if (!v) {
        err = "Package name is required.";
      } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(v)) {
        err = "Package name can only contain letters, numbers, spaces, and hyphens/underscores.";
      } else if (v.length < 2) {
        err = "Package name must be at least 2 characters.";
      } else {
        const vStem = getCleanBaseStem(v);
        const vExact = (v || "").toLowerCase();
        const dupName = existingPackages.find(p => {
          if (p.id === pkg?.id) return false;
          const pExact = (p.name || "").trim().toLowerCase();
          const pStem = getCleanBaseStem(p.name);
          return pExact === vExact || (vStem && pStem && vStem === pStem);
        });
        if (dupName) {
          err = `Package name "${v}" conflicts with existing package "${dupName.name}". Please enter a unique name.`;
        }
      }
    } else if (field === "packageType") {
      if (!v) {
        err = "Package tier is required.";
      } else if (!/^[a-zA-Z0-9\s\-_]+$/.test(v)) {
        err = "Tier can only contain letters, numbers, spaces, and hyphens/underscores.";
      } else {
        const vStem = getCleanBaseStem(v);
        const vExact = (v || "").toLowerCase();
        const dupTier = existingPackages.find(p => {
          if (p.id === pkg?.id) return false;
          const pExact = (p.packageType || "").trim().toLowerCase();
          const pStem = getCleanBaseStem(p.packageType);
          return pExact === vExact || (vStem && pStem && vStem === pStem);
        });
        if (dupTier) {
          err = `Tier "${v}" conflicts with existing tier "${dupTier.packageType || dupTier.name}". Each tier must be unique.`;
        }
      }
    } else if (field === "durationInDays") {
      const dur = Number(v);
      if (v === "" || v === null || v === undefined) {
        err = "Duration is required.";
      } else if (isNaN(dur) || dur <= 0 || !Number.isInteger(dur)) {
        err = "Duration must be a positive whole number of days (greater than 0).";
      }
    } else if (field === "price") {
      const pr = Number(v);
      const isFree = isFreeTierHelper(currentForm.packageType, currentForm.name);
      if (v === "" || v === null || v === undefined) {
        err = "Original price is required.";
      } else if (isNaN(pr)) {
        err = "Original price must be a valid number.";
      } else if (!isFree && pr <= 0) {
        err = "Original price must be greater than ₹0 for paid plans (only Free Trial can be ₹0).";
      } else if (isFree && pr < 0) {
        err = "Original price cannot be negative.";
      } else {
        const dupPrice = existingPackages.find(
          p => p.id !== pkg?.id && Number(p.price) === pr
        );
        if (dupPrice) {
          err = `Original price ₹${pr} is already assigned to package "${dupPrice.name}". Please enter a distinct price.`;
        }
      }
    } else if (field === "discountPercentage") {
      if (v !== "" && v !== null && v !== undefined) {
        const disc = Number(v);
        if (isNaN(disc) || disc < 0 || disc > 100) {
          err = "Discount percentage must be between 0% and 100%.";
        }
      }
    } else if (field === "aiDietGenerationLimit") {
      if (v !== "" && v !== null && v !== undefined) {
        const lim = Number(v);
        if (isNaN(lim) || lim < 0 || !Number.isInteger(lim)) {
          err = "AI Diet generation limit must be a non-negative whole number (0 or greater).";
        }
      }
    } else if (field === "badgeTag") {
      if (v && !/^[a-zA-Z0-9\s%_!\-&+]*$/.test(v)) {
        err = "Badge Tag can only contain letters, numbers, spaces, and %, -, _, !, &, +.";
      } else if (v && v.length > 30) {
        err = "Badge Tag cannot exceed 30 characters.";
      }
    } else if (field === "description") {
      if (v && !/^[a-zA-Z0-9\s.,!?'"()\-_&/%:;+*#@\n]*$/.test(v)) {
        err = "Description contains invalid special characters. Only standard punctuation is permitted.";
      } else if (v && v.length > 500) {
        err = "Description cannot exceed 500 characters.";
      }
    }

    return err;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const err = validateField(field, form[field]);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  const handleChange = (field, value) => {
    const updatedForm = { ...form, [field]: value };
    setForm(updatedForm);
    setTouched(prev => ({ ...prev, [field]: true }));
    const err = validateField(field, value, updatedForm);
    setErrors(prev => {
      const nextErrs = { ...prev, [field]: err };
      if (field === "name" || field === "packageType") {
        if (touched.price || updatedForm.price !== "") {
          nextErrs.price = validateField("price", updatedForm.price, updatedForm);
        }
      }
      return nextErrs;
    });
  };

  const validateAll = () => {
    const fieldsToValidate = ["name", "packageType", "durationInDays", "price", "discountPercentage", "aiDietGenerationLimit", "badgeTag", "description"];
    const newErrors = {};
    const newTouched = {};

    fieldsToValidate.forEach(field => {
      newTouched[field] = true;
      const err = validateField(field, form[field]);
      if (err) newErrors[field] = err;
    });

    setTouched(prev => ({ ...prev, ...newTouched }));
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateAll()) {
      showToast("Please correct the highlighted validation errors before saving.", "error");
      return;
    }

    const featureLines = (form.featuresText || "")
      .split("\n")
      .map(s => s.trim())
      .filter(Boolean);

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        packageType: form.packageType.trim(),
        description: form.description.trim(),
        badgeTag: form.badgeTag.trim(),
        featuresJson: JSON.stringify(featureLines),
        allowedFeatures: allowedFeatures,
        accessLevel: form.accessLevel !== "" && form.accessLevel !== null ? Number(form.accessLevel) : null,
        durationInDays: Number(form.durationInDays),
        price: Number(form.price),
        discountPercentage: form.discountPercentage !== "" ? Number(form.discountPercentage) : 0,
        aiDietGenerationLimit: form.aiDietGenerationLimit !== "" && form.aiDietGenerationLimit !== null ? Number(form.aiDietGenerationLimit) : 5,
      };

      if (pkg?.id) {
        await adminApi.updatePackage(pkg.id, payload);
        showToast("Package updated successfully!");
      } else {
        await adminApi.createPackage(payload);
        showToast("New package created successfully!");
      }
      packageService.notifyPackageChange();
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      showToast(err.message || "Failed to save package.", "error");
    } finally {
      setSaving(false);
    }
  };

  const getInpStyle = (fieldName) => {
    const hasErr = touched[fieldName] && errors[fieldName];
    const isVal = touched[fieldName] && !errors[fieldName] && form[fieldName];
    return {
      width: "100%",
      padding: "10px 14px",
      borderRadius: 13,
      border: hasErr ? "1.5px solid #EF4444" : isVal ? "1.5px solid #22C55E" : "1px solid rgba(239,229,216,0.9)",
      background: hasErr ? "rgba(254,242,242,0.6)" : "rgba(255,255,255,0.9)",
      fontFamily: "'Inter',sans-serif",
      fontSize: 13,
      color: "#1F1F1F",
      outline: "none",
      boxSizing: "border-box",
      transition: "all 0.2s",
    };
  };

  return createPortal(
    <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      style={{ background:"rgba(15,15,15,0.55)", backdropFilter:"blur(8px)" }}
      onClick={onClose}>
      <motion.div initial={{ scale:0.95, opacity:0 }} animate={{ scale:1, opacity:1 }}
        exit={{ scale:0.95, opacity:0 }} transition={{ duration:0.28, ease:[0.25,1,0.5,1] }}
        className="relative overflow-hidden rounded-[32px] p-7 sm:p-9 max-w-3xl w-full my-auto"
        style={{ background:"rgba(255,253,248,0.98)", boxShadow:"0 40px 100px rgba(0,0,0,0.3)", border:"1px solid rgba(255,255,255,0.95)" }}
        onClick={e => e.stopPropagation()}>
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-[13px] flex items-center justify-center text-white"
              style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})`, boxShadow:`0 5px 14px rgba(242,154,12,0.4)` }}>
              <FiTag size={18}/>
            </div>
            <div>
              <p className="font-black text-[18px] text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif" }}>
                {pkg ? "Edit Package" : "Create New Package"}
              </p>
              <p className="text-[12px] text-zinc-400">Configure package tier, standard pricing, and member benefits</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-[10px] flex items-center justify-center border-none cursor-pointer bg-zinc-200/60 text-zinc-600 hover:bg-zinc-300 transition-colors">
            <FiX size={15}/>
          </button>
        </div>

        {/* Modal Tabs Switcher Bar */}
        <div className="flex items-center gap-2 p-1.5 rounded-2xl bg-zinc-100/90 mb-4 border border-zinc-200/80">
          <button
            type="button"
            onClick={() => setActiveModalTab("pricing")}
            className={`flex-1 py-2 rounded-xl text-[12px] font-extrabold transition-all cursor-pointer flex items-center justify-center gap-2 border-none ${
              activeModalTab === "pricing"
                ? "bg-white text-zinc-900 shadow-md ring-1 ring-zinc-200"
                : "text-zinc-500 hover:text-zinc-900"
            }`}
          >
            <FiTag size={14} className={activeModalTab === "pricing" ? "text-amber-500" : ""} />
            Basic & Pricing Info
          </button>
          <button
            type="button"
            onClick={() => setActiveModalTab("permissions")}
            className={`flex-1 py-2 rounded-xl text-[12px] font-extrabold transition-all cursor-pointer flex items-center justify-center gap-2 border-none ${
              activeModalTab === "permissions"
                ? "bg-white text-zinc-900 shadow-md ring-1 ring-zinc-200"
                : "text-zinc-500 hover:text-zinc-900"
            }`}
          >
            <FiLock size={14} className={activeModalTab === "permissions" ? "text-amber-500" : ""} />
            Page Access Permissions
            <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-100 text-amber-800 ml-1">
              {allowedFeatures.length} Active
            </span>
          </button>
        </div>

        <div className="flex flex-col gap-4 mb-6 max-h-[56vh] overflow-y-auto pr-1.5 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth:"none", msOverflowStyle:"none" }}>
          {activeModalTab === "pricing" ? (
            <>
              {/* Row 1: Package Name & Tier */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Package Name */}
                <div>
                  <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>
                    Package Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={form.name}
                      onChange={e => handleChange("name", e.target.value)}
                      onBlur={() => handleBlur("name")}
                      placeholder="e.g. Annual Yoga VIP"
                      style={getInpStyle("name")}
                    />
                    {touched.name && !errors.name && form.name && (
                      <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                    )}
                  </div>
                  {touched.name && errors.name ? (
                    <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.name}
                    </p>
                  ) : (
                    <p className="text-[10px] text-zinc-400 mt-1 font-medium">Alphanumeric package title (e.g. Annual Yoga VIP)</p>
                  )}
                </div>

                {/* Package Tier */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[11px] font-bold text-zinc-700" style={{ fontFamily:"'Inter',sans-serif" }}>
                      Package Tier <span className="text-red-500">*</span>
                    </label>
                    <button type="button" onClick={() => {
                      const nextCustom = !isCustomTier;
                      setIsCustomTier(nextCustom);
                      if (nextCustom) {
                        setForm(f => ({ ...f, packageType: "" }));
                      } else {
                        setForm(f => ({ ...f, packageType: "" }));
                      }
                    }} className="text-[11px] font-bold text-amber-600 hover:underline border-none bg-transparent cursor-pointer">
                      {isCustomTier ? "Select Preset" : "+ Custom Tier"}
                    </button>
                  </div>

                  {isCustomTier ? (
                    <div className="relative">
                      <input
                        type="text"
                        value={form.packageType}
                        onChange={e => handleChange("packageType", e.target.value)}
                        onBlur={() => handleBlur("packageType")}
                        placeholder="e.g. Diamond, Corporate, VIP Pro"
                        style={getInpStyle("packageType")}
                      />
                      {touched.packageType && !errors.packageType && form.packageType && (
                        <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                      )}
                    </div>
                  ) : (
                    <CustomDropdown
                      value={form.packageType}
                      onChange={(val) => {
                        if (val === "CUSTOM_NEW") {
                          setIsCustomTier(true);
                          handleChange("packageType", "");
                        } else {
                          handleChange("packageType", val);
                          if (val) {
                            const lvl = getPresetAccessLevel(val);
                            if (lvl !== "") setForm(f => ({ ...f, accessLevel: lvl }));
                          }
                        }
                      }}
                      options={tierDropdownOptions}
                      label="Tier"
                      icon={FiTag}
                    />
                  )}
                  {touched.packageType && errors.packageType ? (
                    <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.packageType}
                    </p>
                  ) : (
                    <p className="text-[10px] text-zinc-400 mt-1 font-medium">Select canonical tier or enter custom tier level</p>
                  )}
                </div>
              </div>

              {/* Row 2: Duration & Pricing */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Duration */}
                <div>
                  <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>
                    Duration (Days) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={form.durationInDays}
                      onChange={e => handleChange("durationInDays", e.target.value)}
                      onBlur={() => handleBlur("durationInDays")}
                      placeholder="e.g. 30"
                      style={getInpStyle("durationInDays")}
                    />
                    {touched.durationInDays && !errors.durationInDays && form.durationInDays && (
                      <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                    )}
                  </div>
                  {touched.durationInDays && errors.durationInDays ? (
                    <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.durationInDays}
                    </p>
                  ) : (
                    <p className="text-[10px] text-zinc-400 mt-1 font-medium">Validity (e.g. 30, 90, 365)</p>
                  )}
                </div>

                {/* Original Price */}
                <div>
                  <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>
                    Original Price (₹) <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form.price}
                      onChange={e => handleChange("price", e.target.value)}
                      onBlur={() => handleBlur("price")}
                      placeholder="e.g. 1499"
                      style={getInpStyle("price")}
                    />
                    {touched.price && !errors.price && form.price !== "" && (
                      <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                    )}
                  </div>
                  {touched.price && errors.price ? (
                    <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.price}
                    </p>
                  ) : (
                    <p className="text-[10px] text-zinc-400 mt-1 font-medium">Standard INR base price</p>
                  )}
                </div>

                {/* Discount Percentage */}
                <div>
                  <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>
                    Discount (%)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={form.discountPercentage}
                      onChange={e => handleChange("discountPercentage", e.target.value)}
                      onBlur={() => handleBlur("discountPercentage")}
                      placeholder="0"
                      style={getInpStyle("discountPercentage")}
                    />
                    {touched.discountPercentage && !errors.discountPercentage && form.discountPercentage !== "" && (
                      <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                    )}
                  </div>
                  {touched.discountPercentage && errors.discountPercentage ? (
                    <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.discountPercentage}
                    </p>
                  ) : (
                    <p className="text-[10px] text-zinc-400 mt-1 font-medium">Percentage off (0 to 100)</p>
                  )}
                </div>
              </div>

              {/* Pricing calculation preview banner */}
              <div className="rounded-[16px] p-4 bg-amber-500/10 border border-amber-500/20 flex items-center justify-between">
                <div>
                  <span className="text-[13px] font-bold text-amber-900 block">Final Displayed Price</span>
                  <span className="text-[11px] text-amber-700">Calculated after applied discount</span>
                </div>
                <div className="text-right">
                  {numDisc > 0 && numPrice > 0 && (
                    <span className="text-[13px] line-through text-zinc-400 font-bold mr-2">₹{numPrice.toLocaleString()}</span>
                  )}
                  <span className="text-[18px] font-black text-amber-600">₹{discountedPrice.toLocaleString()}</span>
                </div>
              </div>

              {/* Content Access Rank Level & AI Diet Plan Limit */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>
                    Content Access Rank Level
                  </label>
                  <CustomDropdown
                    value={form.accessLevel}
                    onChange={(val) => setForm(f => ({ ...f, accessLevel: val === "" ? "" : Number(val) }))}
                    options={[
                      { label: "Select Access Rank (Optional / Unselected)", value: "" },
                      { label: "Level 0: Free (Free Content Only)", value: 0 },
                      { label: "Level 1: Basic (Free + Basic)", value: 1 },
                      { label: "Level 2: Standard (Free + Basic + Standard)", value: 2 },
                      { label: "Level 3: Premium (Free + Basic + Standard + Premium)", value: 3 },
                      { label: "Level 4: Pro / VIP (Free + Basic + Standard + Premium + Pro)", value: 4 }
                    ]}
                    label="Access Rank"
                    icon={FiStar}
                  />
                  <p className="text-[10px] text-zinc-400 mt-1 font-medium">Determines video library and live class permissions</p>
                </div>

                <div>
                  <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>
                    AI Diet Plan Generation Limit / Month
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={form.aiDietGenerationLimit}
                      onChange={e => handleChange("aiDietGenerationLimit", e.target.value)}
                      onBlur={() => handleBlur("aiDietGenerationLimit")}
                      placeholder="e.g. 5"
                      style={getInpStyle("aiDietGenerationLimit")}
                    />
                    {touched.aiDietGenerationLimit && !errors.aiDietGenerationLimit && form.aiDietGenerationLimit !== "" && (
                      <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                    )}
                  </div>
                  {touched.aiDietGenerationLimit && errors.aiDietGenerationLimit ? (
                    <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.aiDietGenerationLimit}
                    </p>
                  ) : (
                    <p className="text-[10px] text-zinc-400 mt-1 font-medium">Monthly AI generation quota per user (e.g. 5, 10, 30)</p>
                  )}
                </div>
              </div>

              {/* Badge Tag & Description */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>Badge Tag (Optional)</label>
                  <div className="relative">
                    <input
                      value={form.badgeTag}
                      onChange={e => handleChange("badgeTag", e.target.value)}
                      onBlur={() => handleBlur("badgeTag")}
                      placeholder="e.g. 50% OFF, MOST POPULAR"
                      style={getInpStyle("badgeTag")}
                    />
                    {touched.badgeTag && !errors.badgeTag && form.badgeTag && (
                      <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                    )}
                  </div>
                  {touched.badgeTag && errors.badgeTag ? (
                    <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.badgeTag}
                    </p>
                  ) : (
                    <p className="text-[10px] text-zinc-400 mt-1 font-medium">Promotional label (e.g. 50% OFF, POPULAR)</p>
                  )}
                </div>
                <div>
                  <label className="text-[11px] font-bold text-zinc-700 block mb-1.5" style={{ fontFamily:"'Inter',sans-serif" }}>Description</label>
                  <div className="relative">
                    <input
                      value={form.description}
                      onChange={e => handleChange("description", e.target.value)}
                      onBlur={() => handleBlur("description")}
                      placeholder="Brief summary of package benefits..."
                      style={getInpStyle("description")}
                    />
                    {touched.description && !errors.description && form.description && (
                      <FiCheck className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-500" size={14} />
                    )}
                  </div>
                  {touched.description && errors.description ? (
                    <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5">
                      <FiAlertTriangle size={11} className="shrink-0" /> {errors.description}
                    </p>
                  ) : (
                    <p className="text-[10px] text-zinc-400 mt-1 font-medium">Brief summary of package benefits</p>
                  )}
                </div>
              </div>

              {/* Features / Bullet Points */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-bold text-zinc-700" style={{ fontFamily:"'Inter',sans-serif" }}>Package Features / Bullet Points</label>
                  <span className="text-[10px] font-semibold text-amber-600">One feature per line</span>
                </div>
                <textarea
                  rows={3}
                  value={form.featuresText}
                  onChange={e => setForm({ ...form, featuresText: e.target.value })} 
                  placeholder={`Live HD Yoga Classes\nCustom Diet & Nutrition Plan\n100+ Practice Video Library\n1-on-1 Instructor Guidance`}
                  className="w-full rounded-[13px] px-3.5 py-2 text-[12px] outline-none border border-amber-950/20 bg-white text-zinc-800 resize-none font-medium leading-relaxed"
                />
                <p className="text-[10px] text-zinc-400 mt-1 font-medium">Each line is displayed as an active checkmark feature on user pricing & subscription cards.</p>
              </div>
            </>
          ) : (
            /* Tab 2: Tier Page Access Permissions Section */
            <div className="p-4 rounded-[24px] bg-amber-50/70 border border-amber-200/90">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-[14px] font-black text-zinc-900 flex items-center gap-2" style={{ fontFamily: "'Poppins',sans-serif" }}>
                    <FiLock size={16} className="text-amber-600"/> Tier Feature Access Controls
                  </h4>
                  <p className="text-[11px] text-zinc-500 font-medium mt-0.5">
                    Toggle which pages and features are enabled for members on this plan tier. (Default is OFF for new packages).
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setAllowedFeatures(ALL_FEATURE_KEYS.map(f => f.id))}
                    className="px-2.5 py-1 rounded-lg text-[11px] font-extrabold text-emerald-800 bg-emerald-100/80 border border-emerald-300/60 hover:bg-emerald-200 cursor-pointer">
                    Enable All
                  </button>
                  <button type="button" onClick={() => setAllowedFeatures([])}
                    className="px-2.5 py-1 rounded-lg text-[11px] font-extrabold text-rose-800 bg-rose-100/80 border border-rose-300/60 hover:bg-rose-200 cursor-pointer">
                    Disable All
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {ALL_FEATURE_KEYS.filter(f => f.id !== "customise_preferences" && f.id !== "ai_diet_plan").map(feat => {
                  const isChecked = allowedFeatures.includes(feat.id);
                  const Icon = feat.Icon;
                  return (
                    <div key={feat.id}
                      onClick={() => {
                        setAllowedFeatures(prev => isChecked ? prev.filter(k => k !== feat.id) : [...prev, feat.id]);
                      }}
                      className={`p-3 rounded-[16px] border transition-all cursor-pointer flex items-center justify-between select-none ${
                        isChecked ? "bg-white border-amber-400 shadow-xs ring-1 ring-amber-400/40" : "bg-zinc-100/80 border-zinc-200 opacity-65 hover:opacity-100"
                      }`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-8.5 h-8.5 rounded-xl flex items-center justify-center shrink-0 ${isChecked ? "bg-amber-100 text-amber-800" : "bg-zinc-200 text-zinc-500"}`}>
                          <Icon size={16} />
                        </div>
                        <div className="truncate min-w-0">
                          <p className="text-[12px] font-extrabold text-zinc-900 truncate">{feat.label}</p>
                          <p className="text-[10px] text-zinc-500 truncate">{feat.desc}</p>
                        </div>
                      </div>

                      <div className={`w-9.5 h-5 rounded-full p-0.5 transition-colors shrink-0 ml-2 ${isChecked ? "bg-emerald-500" : "bg-zinc-300"}`}>
                        <div className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform ${isChecked ? "translate-x-4.5" : "translate-x-0"}`}/>
                      </div>
                    </div>
                  );
                })}

                {/* Customise Preferences Card */}
                {(() => {
                  const isPrefChecked = allowedFeatures.includes("customise_preferences");
                  return (
                    <div
                      onClick={() => {
                        if (isPrefChecked) {
                          setAllowedFeatures(prev => prev.filter(k => k !== "customise_preferences" && k !== "ai_diet_plan"));
                        } else {
                          setAllowedFeatures(prev => [...prev, "customise_preferences"]);
                        }
                      }}
                      className={`p-3 rounded-[16px] border transition-all cursor-pointer flex items-center justify-between select-none ${
                        isPrefChecked ? "bg-white border-amber-400 shadow-xs ring-1 ring-amber-400/40" : "bg-zinc-100/80 border-zinc-200 opacity-65 hover:opacity-100"
                      }`}
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-8.5 h-8.5 rounded-xl flex items-center justify-center shrink-0 ${isPrefChecked ? "bg-amber-100 text-amber-800" : "bg-zinc-200 text-zinc-500"}`}>
                          <FiSliders size={16} />
                        </div>
                        <div className="truncate min-w-0">
                          <p className="text-[12px] font-extrabold text-zinc-900 truncate">Customise Preferences</p>
                          <p className="text-[10px] text-zinc-500 truncate">Show button on Diet Plan</p>
                        </div>
                      </div>

                      <div className={`w-9.5 h-5 rounded-full p-0.5 transition-colors shrink-0 ml-2 ${isPrefChecked ? "bg-emerald-500" : "bg-zinc-300"}`}>
                        <div className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform ${isPrefChecked ? "translate-x-4.5" : "translate-x-0"}`}/>
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Minimal Clean AI Sub-options when Customise Preferences is ON */}
              {allowedFeatures.includes("customise_preferences") && (
                <div className="mt-3 p-3 rounded-[16px] bg-white border border-amber-300 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div
                    onClick={() => {
                      const isAi = allowedFeatures.includes("ai_diet_plan");
                      setAllowedFeatures(prev => isAi ? prev.filter(k => k !== "ai_diet_plan") : [...prev, "ai_diet_plan"]);
                    }}
                    className="flex items-center gap-2.5 cursor-pointer select-none min-w-0"
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${allowedFeatures.includes("ai_diet_plan") ? "bg-orange-100 text-orange-600" : "bg-zinc-100 text-zinc-400"}`}>
                      <FiZap size={16} />
                    </div>
                    <div>
                      <p className="text-[12px] font-extrabold text-zinc-900">AI Diet Plan Generator</p>
                      <p className="text-[10px] text-zinc-500">Allow generating 7-day plans with AI</p>
                    </div>
                    <div className={`w-9 h-5 rounded-full p-0.5 transition-colors shrink-0 ml-2 ${allowedFeatures.includes("ai_diet_plan") ? "bg-emerald-500" : "bg-zinc-300"}`}>
                      <div className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform ${allowedFeatures.includes("ai_diet_plan") ? "translate-x-4" : "translate-x-0"}`}/>
                    </div>
                  </div>

                  {allowedFeatures.includes("ai_diet_plan") && (
                    <div className="flex items-center gap-2 sm:pl-3 sm:border-l border-zinc-200">
                      <label className="text-[11px] font-bold text-zinc-700 whitespace-nowrap">Monthly Limit:</label>
                      <input
                        type="number"
                        min="0"
                        max="999"
                        value={form.aiDietGenerationLimit}
                        onChange={e => handleChange("aiDietGenerationLimit", e.target.value)}
                        className="w-16 text-center font-bold text-[12px] px-2 py-1 rounded-lg border border-amber-300 bg-amber-50/50 text-zinc-900 outline-none focus:ring-1 focus:ring-amber-500"
                        placeholder="5"
                      />
                      <span className="text-[11px] font-medium text-zinc-400">plans</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button disabled={saving} onClick={handleSubmit}
            className="flex-1 h-11 rounded-full text-white font-bold text-[13px] flex items-center justify-center gap-2 border-none cursor-pointer shadow-lg hover:opacity-95 transition-opacity"
            style={{ background:`linear-gradient(135deg,${T.orange},${T.orange2})` }}>
            {saving ? "Saving..." : (pkg ? "Update Package" : "Create Package")}
          </button>
          <button onClick={onClose} className="h-11 px-6 rounded-full text-[13px] font-semibold border border-amber-950/20 bg-white text-zinc-700 cursor-pointer hover:bg-zinc-50 transition-colors">
            Cancel
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function SubscriptionsPage() {
  const { tiers: dynamicTiers } = useDynamicPackages();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "all";
  const initialStatus = searchParams.get("status") || "all";

  const [mainView, setMainView] = useState("subs"); // "subs" | "packages"
  const [activeTab, setActiveTab] = useState(initialTab === "pending" ? "pending" : (initialTab === "cancelled" ? "cancelled" : "all"));
  const [statusFilter, setStatusFilter] = useState(initialStatus === "expiring" ? "expiring_soon" : (initialStatus !== "all" ? initialStatus : (initialTab === "active" ? "active" : (initialTab === "cancelled" ? "cancelled" : "all"))));
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [dateRangeFilter, setDateRangeFilter] = useState("all");
  const [sortCol, setSortCol] = useState("name");
  const [sortDir, setSortDir] = useState(1);
  const [drawerSub, setDrawerSub] = useState(null);
  const [actionModal, setActionModal] = useState(null); // { type: "extend"|"upgrade"|"invoice"|"refund"|"cancel", sub }
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showPkgModal, setShowPkgModal] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);
  const [packages, setPackages] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [realSubscriptions, setRealSubscriptions] = useState([]);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [dbStats, setDbStats] = useState({
    monthlyRevenue: 0,
    activePlansCount: 0,
    renewalRevenue: 0,
    pendingCount: 0,
    failedCount: 0,
    planDistribution: [],
    pendingCashActivations: []
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [toast, setToast] = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3200); };

  // Normalized, deduplicated Plan / Tier options for filter dropdown
  const uniqueTierOptions = useMemo(() => {
    const seen = new Set(["free trial"]);
    const list = [
      { label: "All Plans", value: "all" },
      { label: "Free Trial", value: "Free Trial" }
    ];

    (packages || []).filter(p => p.isActive !== false).forEach(pkg => {
      const rawName = pkg.packageType || pkg.name || "";
      const canonical = packageService.normalizeTierName(rawName);
      if (canonical && !seen.has(canonical.toLowerCase())) {
        seen.add(canonical.toLowerCase());
        list.push({ label: canonical, value: canonical });
      }
    });

    return list;
  }, [packages]);

  // Helper for matching subscription tier strings robustly across dropdown and 3D cards:
  const matchTier = (s, targetTierName) => {
    if (!s || !targetTierName || targetTierName === "all") return true;
    const sTier = (s.tier || s.packageName || s.package || s.packageType || "").toLowerCase().trim();
    const fKey = targetTierName.toLowerCase().trim();

    if (sTier === fKey) return true;
    if (s.packageId && packages.some(p => p.id === s.packageId && (p.name.toLowerCase() === fKey || p.packageType.toLowerCase() === fKey))) return true;

    const cleanKey = fKey.replace(" plan", "").replace(" trial", "").trim();
    const cleanSTier = sTier.replace(" plan", "").replace(" trial", "").trim();

    if (cleanSTier === cleanKey) return true;
    if (cleanSTier && cleanKey && (cleanSTier.includes(cleanKey) || cleanKey.includes(cleanSTier))) return true;

    return false;
  };

  const loadSubscriptionsData = async () => {
    try {
      const [pkgRes, statsRes, subRes, usersRes, analyticsRes] = await Promise.all([
        adminApi.getPackages().catch(() => null),
        adminApi.getSubscriptionStats().catch(() => null),
        adminApi.getSubscriptions({ status: activeTab, search, page, period: dateRangeFilter }).catch(() => null),
        adminApi.getUsers({ pageSize: 200 }).catch(() => null),
        adminApi.getAnalytics("12m").catch(() => null),
      ]);
      if (pkgRes?.data) setPackages(pkgRes.data?.data || pkgRes.data?.Data || pkgRes.data || []);
      
      const statsPayload = statsRes?.data?.data || statsRes?.data?.Data || statsRes?.data || {};
      const analyticsKpi = analyticsRes?.data?.data?.kpi || analyticsRes?.data?.kpi || {};
      const lifeTimeTotalRev = analyticsKpi.totalRev ?? analyticsKpi.totalRevenue ?? analyticsKpi.TotalRev;

      setDbStats({
        ...statsPayload,
        totalRevenue: statsPayload.totalRevenue ?? statsPayload.TotalRevenue ?? lifeTimeTotalRev,
        totalRev: lifeTimeTotalRev ?? statsPayload.totalRevenue ?? statsPayload.TotalRevenue
      });

      const userList = usersRes?.data?.users || usersRes?.data || [];
      const avatarMap = new Map();
      userList.forEach(u => {
        const img = u.profileImageUrl || u.avatarUrl || u.userProfileImage;
        if (img) {
          if (u.id) avatarMap.set(String(u.id), img);
          if (u.email) avatarMap.set(u.email.toLowerCase(), img);
          if (u.fullName) avatarMap.set(u.fullName.toLowerCase(), img);
        }
      });

      if (subRes?.data) {
        const list = subRes.data.subscriptions || subRes.data.Subscriptions || (Array.isArray(subRes.data) ? subRes.data : []);
        const enrichedList = list.map(s => {
          const matchedAvatar = 
            s.profileImageUrl || s.avatarUrl || s.userProfileImage || s.user?.profileImageUrl || s.user?.avatarUrl ||
            (s.userId && avatarMap.get(String(s.userId))) ||
            (s.email && avatarMap.get(s.email.toLowerCase())) ||
            (s.name && avatarMap.get(s.name.toLowerCase()));

          return {
            ...s,
            profileImageUrl: matchedAvatar || null
          };
        });
        setRealSubscriptions(enrichedList);
        setTotalPages(subRes.data.totalPages || subRes.data.TotalPages || 1);
      }
    } catch {
      // safe fallback
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptionsData();
  }, [activeTab, search, page, dateRangeFilter]);

  const [showDiscontinued, setShowDiscontinued] = useState(false);

  // Custom Super Admin Package Confirmation Modal State
  const [pkgConfirmModal, setPkgConfirmModal] = useState({
    isOpen: false,
    title: "",
    packageName: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    isDanger: true,
    pkg: null,
    actionType: null, // "delete" | "toggle"
  });

  const promptTogglePkgStatus = (pkg) => {
    const isDeactivating = pkg.isActive !== false;
    setPkgConfirmModal({
      isOpen: true,
      title: isDeactivating ? "Deactivate Package" : "Re-activate Package",
      packageName: pkg.name,
      message: isDeactivating
        ? `Are you sure you want to deactivate "${pkg.name}"? It will be hidden from public checkout and new user signups, but existing subscribers retain their plan.`
        : `Are you sure you want to re-activate "${pkg.name}"? It will become visible to new users on the pricing page and plan dropdowns.`,
      confirmText: isDeactivating ? "Deactivate Plan" : "Re-activate Plan",
      cancelText: "Cancel",
      isDanger: isDeactivating,
      pkg,
      actionType: "toggle",
    });
  };

  const promptDeletePkg = (pkg) => {
    setPkgConfirmModal({
      isOpen: true,
      title: "Delete Package Tier",
      packageName: pkg.name,
      message: `Are you sure you want to permanently delete package tier "${pkg.name}"? This card will immediately disappear from the screen and hide this package from all selection pages and dropdowns across the application.`,
      confirmText: "Confirm Delete",
      cancelText: "Cancel",
      isDanger: true,
      pkg,
      actionType: "delete",
    });
  };

  const promptResetTestData = () => {
    setPkgConfirmModal({
      isOpen: true,
      title: "Reset Test Subscription Data",
      packageName: null,
      message: "Are you sure you want to reset all test subscription data? This will clear test subscription rows from the table without deleting user accounts.",
      confirmText: "Reset Test Data",
      cancelText: "Cancel",
      isDanger: true,
      pkg: { id: "test-data" },
      actionType: "reset_test_data",
    });
  };

  const handleExecutePkgAction = async () => {
    if (!pkgConfirmModal.pkg) return;
    const { pkg, actionType } = pkgConfirmModal;
    setPkgConfirmModal((prev) => ({ ...prev, isOpen: false }));

    if (actionType === "reset_test_data") {
      try {
        await adminApi.resetSubscriptions();
        showToast("All test subscription records reset successfully!");
        loadSubscriptionsData();
      } catch (err) {
        showToast(err.message || "Failed to reset test subscription data.", "error");
      }
      return;
    }

    if (actionType === "delete") {
      try {
        setPackages((prev) => prev.filter((p) => p.id !== pkg.id));
        await adminApi.hardDeletePackage(pkg.id).catch(async () => {
          await adminApi.deletePackage(pkg.id);
        });
        showToast(`Package "${pkg.name}" deleted successfully.`);
        packageService.notifyPackageChange();
        loadSubscriptionsData();
      } catch (err) {
        showToast(err.message || "Failed to delete package.", "error");
        loadSubscriptionsData();
      }
    } else if (actionType === "toggle") {
      const isDeactivating = pkg.isActive !== false;
      try {
        await adminApi.togglePackageStatus(pkg.id);
        showToast(
          isDeactivating
            ? `Package "${pkg.name}" deactivated.`
            : `Package "${pkg.name}" re-activated successfully!`
        );
        packageService.notifyPackageChange();
        loadSubscriptionsData();
      } catch (err) {
        showToast(err.message || "Failed to update package status.", "error");
      }
    }
  };

  // One-click cash approval
  const handleOneClickActivateCash = async (sub) => {
    setActionLoadingId(sub.id);
    try {
      if (sub.id && sub.id < 90000) {
        await adminApi.extendSubscription({ subscriptionId: sub.id, userId: sub.userId, days: 30 });
      } else if (sub.userId) {
        const matchedPkg = packages.find(p => p.id === sub.packageId || p.name === sub.tier) || packages[0];
        await adminApi.createSubscription({
          userId: sub.userId,
          packageId: matchedPkg?.id || 1,
          startDate: new Date().toISOString(),
          amountPaid: sub.amount || matchedPkg?.price || 0,
          paymentStatus: "Completed",
          transactionId: `CASH-APPROVED-${Date.now()}`
        });
      }
      showToast(`✓ Cash subscription for ${sub.name || "User"} activated successfully!`);
      loadSubscriptionsData();
    } catch (err) {
      showToast(err.message || "Failed to activate subscription.", "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  // One-click quick renewal (+30 days) for expired plans
  const handleOneClickRenewExpired = async (sub) => {
    setActionLoadingId(sub.id);
    try {
      await adminApi.extendSubscription({ subscriptionId: sub.id, userId: sub.userId, days: 30 });
      showToast(`✓ Plan extended by 30 days for ${sub.name || "User"}!`);
      loadSubscriptionsData();
    } catch (err) {
      showToast(err.message || "Failed to extend subscription.", "error");
    } finally {
      setActionLoadingId(null);
    }
  };

  // Dedicated lists for Pending Cash & Expired Subscriptions
  const pendingCashSubscriptions = useMemo(() => {
    const source = (realSubscriptions && realSubscriptions.length > 0) ? realSubscriptions : [];
    return source.filter(s => 
      s.status === "Pending" ||
      (s.method === "Cash" && (s.status === "Pending" || s.paymentStatus === "Pending")) ||
      (s.txId && s.txId.startsWith("MANUAL") && s.status === "Pending")
    );
  }, [realSubscriptions]);

  const expiredSubscriptions = useMemo(() => {
    const source = (realSubscriptions && realSubscriptions.length > 0) ? realSubscriptions : [];
    return source.filter(s => 
      s.status === "Expired" || 
      (typeof s.daysLeft === "number" && s.daysLeft <= 0 && s.status !== "Active" && s.status !== "Free Trial")
    );
  }, [realSubscriptions]);

  // Dynamic counts for right-side filter tabs
  const statusCounts = useMemo(() => {
    const source = (realSubscriptions && realSubscriptions.length > 0) ? realSubscriptions : [];
    const latestUserMap = new Map();
    source.forEach(s => {
      const userKey = s.userId || s.email || s.name;
      if (!userKey) return;
      if (!latestUserMap.has(userKey) || Number(s.id || 0) > Number(latestUserMap.get(userKey).id || 0)) {
        latestUserMap.set(userKey, s);
      }
    });
    const uniqueList = Array.from(latestUserMap.values());

    let active = 0;
    let expiring_soon = 0;
    let cancelled = 0;
    let expired = 0;

    uniqueList.forEach(s => {
      const st = (s.status || "Active").toLowerCase();
      const days = typeof s.daysLeft === "number" ? s.daysLeft : 30;
      if (st.includes("cancel") || st.includes("refund")) {
        cancelled++;
      } else if (st === "expired" || days <= 0) {
        expired++;
      } else if (days <= 7) {
        expiring_soon++;
        active++;
      } else {
        active++;
      }
    });

    return {
      all: uniqueList.length,
      active,
      expiring_soon,
      cancelled,
      expired
    };
  }, [realSubscriptions]);

  const filtered = useMemo(() => {
    const source = (realSubscriptions && realSubscriptions.length > 0) ? realSubscriptions : [];

    // Group by User to select ONLY the latest subscription record for each user in the main table:
    const latestUserMap = new Map();
    source.forEach(s => {
      const userKey = s.userId || s.email || s.name;
      if (!userKey) return;
      if (!latestUserMap.has(userKey)) {
        latestUserMap.set(userKey, s);
      } else {
        const existing = latestUserMap.get(userKey);
        const existingId = Number(existing.id || 0);
        const currentId = Number(s.id || 0);
        if (currentId > existingId) {
          latestUserMap.set(userKey, s);
        }
      }
    });

    const latestSubscriptions = latestUserMap.size > 0 ? Array.from(latestUserMap.values()) : source;

    let data = latestSubscriptions.filter(s => {
      const q = search.toLowerCase();
      const nameMatch = (s.name || s.userFullName || "").toLowerCase().includes(q);
      const emailMatch = (s.email || s.userEmail || "").toLowerCase().includes(q);
      const tierMatch = (s.tier || s.packageName || "").toLowerCase().includes(q);
      const passSearch = (!q || nameMatch || emailMatch || tierMatch);

      const passTier = (!tierFilter || tierFilter === "all") ? true : matchTier(s, tierFilter);

      const passMethod = (() => {
        if (!methodFilter || methodFilter === "all") return true;
        const sMethod = (s.method || s.paymentMethod || "").toLowerCase();
        const filterKey = methodFilter.toLowerCase();
        if (filterKey === "online") return sMethod.includes("online") || (s.txId && !s.txId.startsWith("MANUAL"));
        if (filterKey === "cash") return sMethod.includes("cash") || (s.txId && s.txId.startsWith("MANUAL"));
        return sMethod.includes(filterKey);
      })();

      const passDateRange = (() => {
        if (!dateRangeFilter || dateRangeFilter === "all") return true;
        
        const dateObj = (() => {
          const raw = s.createdAt || s.start || s.startDate || s.createdDate || s.date;
          if (!raw) return null;
          const d = new Date(raw);
          return isNaN(d.getTime()) ? null : d;
        })();

        if (!dateObj) return true;

        const now = new Date();
        const diffDays = (now.getTime() - dateObj.getTime()) / (1000 * 60 * 60 * 24);

        if (dateRangeFilter === "this_month") {
          const isSameCalendarMonth = (dateObj.getMonth() === now.getMonth()) && (dateObj.getFullYear() === now.getFullYear());
          return isSameCalendarMonth || (diffDays >= -7 && diffDays <= 31);
        }
        if (dateRangeFilter === "2_months") return diffDays >= -7 && diffDays <= 60;
        if (dateRangeFilter === "3_months") return diffDays >= -7 && diffDays <= 90;
        if (dateRangeFilter === "6_months") return diffDays >= -7 && diffDays <= 180;
        if (dateRangeFilter === "1_year") return diffDays >= -7 && diffDays <= 365;

        return true;
      })();

      const passStatus = (() => {
        if (!statusFilter || statusFilter === "all") return true;
        const sStatus = (s.status || "Active").toLowerCase();
        const days = typeof s.daysLeft === "number" ? s.daysLeft : 30;
        
        if (statusFilter === "active") {
          return (sStatus === "active" || sStatus === "completed" || sStatus === "free trial") && days > 0;
        }
        if (statusFilter === "expiring_soon") {
          return days > 0 && days <= 7;
        }
        if (statusFilter === "cancelled") {
          return sStatus.includes("cancel") || sStatus.includes("refund");
        }
        if (statusFilter === "expired") {
          return sStatus === "expired" || days <= 0;
        }
        return true;
      })();

      return passSearch && passTier && passMethod && passDateRange && passStatus;
    });

    if (sortCol) {
      data = [...data].sort((a, b) => {
        let valA = a[sortCol] ?? "";
        let valB = b[sortCol] ?? "";
        if (typeof valA === "string") valA = valA.toLowerCase();
        if (typeof valB === "string") valB = valB.toLowerCase();
        if (valA < valB) return -1 * sortDir;
        if (valA > valB) return 1 * sortDir;
        return 0;
      });
    }

    return data;
  }, [realSubscriptions, search, tierFilter, methodFilter, statusFilter, dateRangeFilter, sortCol, sortDir]);

  // Client-side Pagination (10 rows per page):
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [tierFilter, methodFilter, search]);

  const totalPagesCount = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));

  const paginatedSubscriptions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  const handleTierCardClick = (tierName) => {
    const isCurrent = tierFilter !== "all" && matchTier({ tier: tierName }, tierFilter);
    if (isCurrent) {
      setTierFilter("all");
    } else {
      setTierFilter(tierName);
    }
    setCurrentPage(1);
    const tableEl = document.getElementById("subscriptions-table-section");
    if (tableEl) {
      tableEl.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d * -1);
    else { setSortCol(col); setSortDir(1); }
  };

  const SortTH = ({ col, label }) => (
    <th onClick={() => handleSort(col)}
      className="text-[10px] font-black uppercase tracking-[0.9px] text-left cursor-pointer select-none whitespace-nowrap py-2.5 px-3.5 transition-colors"
      style={{ color:sortCol === col ? T.olive : T.light, borderBottom:`1px solid rgba(239,229,216,0.6)` }}>
      <div className="flex items-center gap-1">
        {label}
        <FiArrowUp size={10} style={{ opacity:sortCol === col ? 1 : 0.3, transform:sortCol === col && sortDir === -1 ? "rotate(180deg)" : "none", transition:"transform 0.2s" }}/>
      </div>
    </th>
  );

  if (isInitialLoading && realSubscriptions.length === 0 && packages.length === 0) {
    return (
      <AdminDashboardLayout activeNav="subscriptions">
        <BrandLoader 
          message="Loading Subscriptions & Packages..." 
          subMessage="Fetching member subscriptions, packages, and transaction logs" 
        />
      </AdminDashboardLayout>
    );
  }

  return (
    <AdminDashboardLayout activeNav="subscriptions">
      <div style={{ fontFamily:"'Inter',sans-serif" }}>
        <div className="relative z-10 w-full min-w-0 text-[#1F1F1F]">
        {/* Topbar */}
        <motion.div initial={{ opacity:0, y:-16 }} animate={{ opacity:1, y:0 }}
          className="ad-toolbar flex items-center justify-between mb-6">
          <div>
            <h1 className="font-black text-[#1F1F1F]" style={{ fontFamily:"'Poppins',sans-serif", fontSize:26, letterSpacing:"-1px" }}>Subscriptions & Packages</h1>
            <div className="ad-scroll flex items-center gap-3 mt-2">
              <button onClick={() => setMainView("subs")}
                className={`px-4 py-1.5 rounded-full text-[12px] font-bold cursor-pointer transition-all border-none ${mainView === "subs" ? "bg-amber-500 text-white shadow-md" : "bg-white text-zinc-600 border border-zinc-200"}`}>
                Subscriptions List
              </button>
              <button onClick={() => setMainView("packages")}
                className={`px-4 py-1.5 rounded-full text-[12px] font-bold cursor-pointer transition-all border-none ${mainView === "packages" ? "bg-amber-500 text-white shadow-md" : "bg-white text-zinc-600 border border-zinc-200"}`}>
                Manage Packages ({packages.length})
              </button>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <motion.button whileHover={{ scale:1.02 }} whileTap={{ scale:0.98 }}
              onClick={promptResetTestData}
              className="h-9 px-4 rounded-full text-red-600 bg-red-50 border border-red-200 text-[12px] font-bold flex items-center gap-1.5 cursor-pointer hover:bg-red-100 transition-all shadow-sm">
              <FiTrash2 size={13}/> Reset Test Data
            </motion.button>
            <motion.button whileHover={{ y:-1 }} onClick={() => { setEditingPkg(null); setShowPkgModal(true); }}
              className="h-9 px-4 rounded-full text-[12px] font-bold flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 cursor-pointer">
              <FiPlus size={14}/> Create Package
            </motion.button>
            <motion.button whileHover={{ y:-2 }} whileTap={{ scale:0.97 }} onClick={() => setShowActivateModal(true)}
              className="h-9 px-5 rounded-full text-white text-[12px] font-bold flex items-center gap-1.5"
              style={{ background:`linear-gradient(135deg,${T.olive},${T.olive2})`, boxShadow:`0 5px 16px rgba(107,138,90,0.38)`, border:"none", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              <FiCheck size={13}/> Assign to User
            </motion.button>
          </div>
        </motion.div>

        {mainView === "packages" ? (
          <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} className="mb-8">
            {(() => {
              const visiblePackages = showDiscontinued ? packages : packages.filter(p => p.isActive !== false);

              return (
                <>
                  {/* Header Toolbar */}
                  <div className="ad-toolbar ad-pad flex items-center justify-between gap-4 mb-6 p-6 rounded-[24px]"
                    style={{ background: "rgba(255,255,255,0.85)", backdropFilter: "blur(16px)", border: "1px solid rgba(239,229,216,0.9)", boxShadow: "0 12px 32px rgba(0,0,0,0.04)" }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-amber-600 bg-amber-50 border border-amber-200/60 shadow-sm">
                        <FiZap size={20}/>
                      </div>
                      <div>
                        <h2 className="text-[20px] font-black text-zinc-900 tracking-tight" style={{ fontFamily:"'Poppins',sans-serif" }}>
                          Subscription Packages ({visiblePackages.length})
                        </h2>
                        <p className="text-[12px] text-zinc-500 font-medium">Manage pricing, duration, badge tags, and features for user subscriptions.</p>
                      </div>
                    </div>

                    <button onClick={() => setShowDiscontinued(prev => !prev)}
                      className={`px-4 py-2 rounded-full text-[12px] font-bold border transition-all cursor-pointer shadow-xs ${
                        showDiscontinued ? "bg-amber-500 text-white border-amber-500" : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                      }`}>
                      {showDiscontinued ? "Hide Discontinued Tiers" : "Show Discontinued Tiers"}
                    </button>
                  </div>

                  {/* Ultra-Premium Package Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    <AnimatePresence mode="popLayout">
                      {visiblePackages.map((pkg, idx) => {
                const isPopular = pkg.badgeTag?.toLowerCase().includes("popular") || idx === 1;
                const parsedFeatures = (() => {
                  try {
                    if (Array.isArray(pkg.features)) return pkg.features;
                    if (Array.isArray(pkg.featuresJson)) return pkg.featuresJson;
                    if (typeof pkg.featuresJson === "string" && pkg.featuresJson.trim()) {
                      try {
                        const parsed = JSON.parse(pkg.featuresJson);
                        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
                      } catch {}
                      const split = pkg.featuresJson.split(/\r?\n|,/).map(s => s.trim()).filter(Boolean);
                      if (split.length > 0) return split;
                    }
                  } catch {
                    // fallback
                  }
                  return ["Full Yoga Access", "Live HD Sessions", "Community Support", "Personal Progress Tracking"];
                })();

                return (
                  <motion.div key={pkg.id || idx} initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ delay: 0.05 * idx }}
                    whileHover={{ y:-4, boxShadow: "0 20px 40px rgba(0,0,0,0.08)" }}
                    className="rounded-[28px] p-6 relative flex flex-col justify-between transition-all duration-300 border"
                    style={{
                      background: isPopular ? "linear-gradient(160deg, rgba(255,255,255,0.95), rgba(255,248,238,0.92))" : "rgba(255,255,255,0.9)",
                      backdropFilter: "blur(16px)",
                      borderColor: isPopular ? "rgba(242,154,12,0.4)" : "rgba(239,229,216,0.9)",
                      boxShadow: isPopular ? "0 14px 36px rgba(242,154,12,0.12)" : "0 10px 28px rgba(0,0,0,0.04)"
                    }}>

                    {/* Top Badge Tag */}
                    {pkg.badgeTag ? (
                      <div className="absolute -top-3.5 left-6 text-[10px] font-black uppercase tracking-wider text-white px-3.5 py-1 rounded-full shadow-md flex items-center gap-1"
                        style={{ background: isPopular ? "linear-gradient(135deg,#F29A0C,#E97818)" : "linear-gradient(135deg,#6B8A5A,#8BA47A)" }}>
                        <FiZap size={11}/> {pkg.badgeTag}
                      </div>
                    ) : isPopular ? (
                      <div className="absolute -top-3.5 left-6 text-[10px] font-black uppercase tracking-wider text-white px-3.5 py-1 rounded-full shadow-md flex items-center gap-1"
                        style={{ background: "linear-gradient(135deg,#F29A0C,#E97818)" }}>
                        <FiZap size={11}/> MOST POPULAR
                      </div>
                    ) : null}

                    <div>
                      {/* Title & Status */}
                      <div className="flex items-center justify-between mt-2 mb-3">
                        <h3 className="font-black text-[18px] text-zinc-900 tracking-tight" style={{ fontFamily:"'Poppins',sans-serif" }}>
                          {pkg.name}
                        </h3>
                        <span className={`text-[10px] font-extrabold px-3 py-1 rounded-full border ${pkg.isActive !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                          {pkg.isActive !== false ? "Active" : "Inactive (Discontinued)"}
                        </span>
                      </div>

                      {/* Price & Discount */}
                      <div className="p-4 rounded-2xl mb-4" style={{ background: "rgba(247,243,237,0.6)", border: "1px solid rgba(239,229,216,0.7)" }}>
                        {pkg.discountPercentage > 0 && (
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[12px] text-zinc-400 line-through font-bold">₹{pkg.price?.toLocaleString()}</span>
                            <span className="text-[10px] font-black text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-full border border-emerald-300">
                              {pkg.discountPercentage}% OFF
                            </span>
                          </div>
                        )}
                        <div className="flex items-baseline gap-1">
                          <span className="font-black text-[26px] text-zinc-900 leading-none" style={{ fontFamily:"'Poppins',sans-serif" }}>
                            ₹{(pkg.discountedPrice || pkg.price)?.toLocaleString()}
                          </span>
                          <span className="text-[12px] font-semibold text-zinc-500">
                            / {pkg.durationInDays || 30} Days
                          </span>
                        </div>
                      </div>

                      {/* Short Description */}
                      {pkg.description && (
                        <p className="text-[12px] text-zinc-600 mb-4 line-clamp-2 leading-relaxed font-medium">
                          {pkg.description}
                        </p>
                      )}

                      {/* Features List */}
                      <div className="space-y-2 mb-6 pt-3 border-t border-zinc-100">
                        <p className="text-[10px] font-black uppercase tracking-wider text-zinc-400 mb-2">Package Features</p>
                        {parsedFeatures.slice(0, 4).map((feat, fIdx) => (
                          <div key={fIdx} className="flex items-center gap-2 text-[12px] font-semibold text-zinc-700">
                            <FiCheckCircle size={14} className="text-emerald-600 shrink-0"/>
                            <span className="truncate">{feat}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 pt-4 border-t border-zinc-100">
                      <button onClick={() => { setEditingPkg(pkg); setShowPkgModal(true); }}
                        className="flex-1 py-2.5 rounded-full text-[12px] font-bold border border-amber-300 bg-amber-50/80 text-amber-900 cursor-pointer hover:bg-amber-100 transition-all flex items-center justify-center gap-1.5 shadow-xs"
                        title="Edit Package Configuration">
                        <FiEdit size={13}/> Edit
                      </button>
                      
                      {/* Active / Inactive Toggle Button */}
                      <button onClick={() => promptTogglePkgStatus(pkg)}
                        className={`px-3.5 py-2.5 rounded-full text-[12px] font-bold border cursor-pointer transition-all flex items-center justify-center gap-1 shadow-xs ${
                          pkg.isActive !== false
                            ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                            : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                        }`}
                        title={pkg.isActive !== false ? "Deactivate Package (Hide from checkout)" : "Re-activate Package (Show on pricing page)"}>
                        {pkg.isActive !== false ? "Deactivate" : "Re-Activate"}
                      </button>

                      {/* Delete Package Tier Button */}
                      <button onClick={() => promptDeletePkg(pkg)}
                        className="px-3 py-2.5 rounded-full text-[12px] font-bold border border-red-200 bg-red-50 text-red-600 cursor-pointer hover:bg-red-100 transition-all flex items-center justify-center shadow-xs"
                        title="Delete Package Tier (Removes from dropdowns & selection pages)">
                        <FiTrash2 size={14}/>
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </>
      );
    })()}
  </motion.div>
        ) : (
          <div>
            {/* KPI strip with REAL DATABASE DATA */}
            {(() => {
              const activePaidSubs = realSubscriptions.filter(s =>
                s.status === "Active" &&
                (s.daysLeft === undefined || s.daysLeft > 0) &&
                (s.amountPaid > 0 || s.amount > 0 || (s.tier && !s.tier.toLowerCase().includes("free")))
              );

              const distinctPaidUsersCount = new Set(
                activePaidSubs.map(s => s.userId || s.email || s.name)
              ).size;

              const rawTotalRev = dbStats?.totalRev ?? dbStats?.totalRevenue ?? dbStats?.TotalRevenue;
              const rawMonthlyRev = dbStats?.monthlyRevenue ?? dbStats?.MonthlyRevenue;
              const rawMonthlyCount = dbStats?.monthlyPaidSubsCount ?? dbStats?.MonthlyPaidSubsCount ?? dbStats?.monthlySubsCount ?? dbStats?.MonthlySubsCount;
              const rawActivePlans = dbStats?.activePlansCount ?? dbStats?.ActivePlansCount;
              const rawFreeTrial = dbStats?.freeTrialCount ?? dbStats?.FreeTrialCount;
              const rawRenewalRev = dbStats?.renewalRevenue ?? dbStats?.RenewalRevenue;
              const rawRenewalCount = dbStats?.renewalCount ?? dbStats?.RenewalCount;

              const activePaidPlansCount = rawActivePlans !== undefined && rawActivePlans !== null
                ? Number(rawActivePlans)
                : Number(distinctPaidUsersCount || 0);

              const freeTrialUsersCount = rawFreeTrial !== undefined && rawFreeTrial !== null
                ? Number(rawFreeTrial)
                : 0;

              const distinctRenewedUsersCount = new Set(
                realSubscriptions
                  .filter(s => (s.amountPaid > 0 || s.amount > 0) && new Date(s.start || s.startDate || s.createdAt).getMonth() === new Date().getMonth())
                  .map(s => s.userId || s.email || s.name)
              ).size;

              const now = new Date();
              const currentMonth = now.getMonth();
              const currentYear = now.getFullYear();

              const monthlyRenewalsCount = rawRenewalCount !== undefined && rawRenewalCount !== null
                ? Number(rawRenewalCount)
                : Number(distinctRenewedUsersCount || 0);

              const renewalRevVal = rawRenewalRev !== undefined && rawRenewalRev !== null
                ? Number(rawRenewalRev)
                : 0;

              const computedTotalRev = realSubscriptions.reduce((acc, s) => acc + (Number(s.amountPaid ?? s.amount ?? 0)), 0);

              const computedMonthlyRev = realSubscriptions.filter(s => {
                const dateStr = s.createdAt || s.startDate || s.start || s.date;
                if (!dateStr) return false;
                const d = new Date(dateStr);
                if (isNaN(d.getTime())) return false;
                return d.getMonth() === currentMonth && d.getFullYear() === currentYear && Number(s.amountPaid ?? s.amount ?? 0) > 0;
              }).reduce((acc, s) => acc + (Number(s.amountPaid ?? s.amount ?? 0)), 0);

              const computedMonthlyCount = realSubscriptions.filter(s => {
                const dateStr = s.createdAt || s.startDate || s.start || s.date;
                if (!dateStr) return false;
                const d = new Date(dateStr);
                if (isNaN(d.getTime())) return false;
                return d.getMonth() === currentMonth && d.getFullYear() === currentYear && Number(s.amountPaid ?? s.amount ?? 0) > 0;
              }).length;

              const totalRevVal = rawTotalRev !== undefined && rawTotalRev !== null
                ? Number(rawTotalRev)
                : Number(computedTotalRev || 0);

              const monthlyRevVal = rawMonthlyRev !== undefined && rawMonthlyRev !== null
                ? Number(rawMonthlyRev)
                : Number(computedMonthlyRev || 0);

              const monthlySubsCount = rawMonthlyCount !== undefined && rawMonthlyCount !== null
                ? Number(rawMonthlyCount)
                : Number(computedMonthlyCount || 0);

              return (
                <div className="ad-kpi grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
                  <KpiCard icon={FiCreditCard} value={`₹${(monthlyRevVal || 0).toLocaleString()}`} label="Monthly Revenue" trend={`${monthlySubsCount} plan${monthlySubsCount !== 1 ? 's' : ''} this month`} iconBg={`linear-gradient(135deg,${T.orange},${T.orange2})`} accentColor={`linear-gradient(90deg,${T.orange},${T.orange2})`} cardBg="linear-gradient(145deg,#FFF4E8,#FFF8EE)" delay={0.06}/>
                  <KpiCard icon={FiTrendingUp} value={`₹${(totalRevVal || 0).toLocaleString()}`} label="Total Revenue" trend="All Time Total" iconBg="linear-gradient(135deg,#16A34A,#15803D)" accentColor="#16A34A" cardBg="linear-gradient(145deg,#F0FDF4,#F6FEF9)" delay={0.08}/>
                  <KpiCard icon={FiUserCheck} value={activePaidPlansCount.toString()} label="Active Paid Plans" trend="Paid Subscribers Only" iconBg={`linear-gradient(135deg,${T.olive},${T.olive2})`} accentColor={`linear-gradient(90deg,${T.olive},${T.olive2})`} cardBg="linear-gradient(145deg,#F5FAF2,#F9FDF5)" delay={0.1}/>
                  <KpiCard icon={FiRefreshCw} value={monthlyRenewalsCount.toString()} label="Monthly Renewals" trend={`₹${(renewalRevVal || 0).toLocaleString()} Renewed`} iconBg="rgba(34,197,94,0.2)" accentColor={T.green} delay={0.14}/>
                  <KpiCard icon={FiUsers} value={freeTrialUsersCount.toString()} label="Free Trial Users" trend="Registered Members" iconBg="linear-gradient(135deg,#6366F1,#8B5CF6)" accentColor="#6366F1" cardBg="linear-gradient(145deg,#EEF2FF,#F5F3FF)" delay={0.12}/>
                  <KpiCard icon={FiAlertCircle} value={(dbStats?.pendingCount ?? dbStats?.PendingCount ?? 0).toString()} label="Pending Activation" trend="Awaiting Activation" trendUp={false} iconBg="rgba(245,158,11,0.2)" accentColor={T.amber} cardBg="linear-gradient(145deg,rgba(245,158,11,0.07),rgba(255,248,232,0.5))" delay={0.18}/>
                </div>
              );
            })()}

            {/* Charts row with REAL DATABASE DATA */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
              {/* Subscription Package Performance Chart (DYNAMIC TIER BREAKDOWN) */}
              <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.1 }}
                className="ad-card lg:col-span-2 rounded-[32px] p-7 relative overflow-hidden flex flex-col justify-between"
                style={{
                  background: "linear-gradient(145deg, rgba(255,255,255,0.99), rgba(250,246,238,0.96))",
                  backdropFilter: "blur(24px)",
                  border: "1px solid rgba(235,225,210,0.85)",
                  boxShadow: "0 20px 50px rgba(0,0,0,0.05), inset 0 1px 1px rgba(255,255,255,1)"
                }}>
                
                {(() => {
                  const PRESET_TIER_STYLES = {
                    "free trial": { badge: "Starter", bg: "linear-gradient(180deg, #FBBF24 0%, #D97706 100%)", glow: "rgba(245,158,11,0.22)", lightBg: "linear-gradient(180deg, rgba(255,251,245,0.95), rgba(254,243,199,0.7))", text: "#B45309", border: "rgba(245,158,11,0.28)", accentBg: "rgba(245,158,11,0.08)" },
                    "basic": { badge: "Popular", bg: "linear-gradient(180deg, #8BA47A 0%, #6B8A5A 50%, #4D683E 100%)", glow: "rgba(107,138,90,0.25)", lightBg: "linear-gradient(180deg, rgba(247,250,245,0.95), rgba(235,243,230,0.75))", text: "#4D683E", border: "rgba(107,138,90,0.3)", accentBg: "rgba(107,138,90,0.1)" },
                    "standard": { badge: "Best Value", bg: "linear-gradient(180deg, #38BDF8 0%, #0284C7 50%, #0369A1 100%)", glow: "rgba(2,132,199,0.22)", lightBg: "linear-gradient(180deg, rgba(240,249,255,0.95), rgba(224,242,254,0.75))", text: "#0369A1", border: "rgba(2,132,199,0.28)", accentBg: "rgba(2,132,199,0.08)" },
                    "premium": { badge: "Pro Level", bg: "linear-gradient(180deg, #34D399 0%, #059669 50%, #047857 100%)", glow: "rgba(5,150,105,0.22)", lightBg: "linear-gradient(180deg, rgba(236,253,245,0.95), rgba(209,250,229,0.75))", text: "#047857", border: "rgba(5,150,105,0.28)", accentBg: "rgba(5,150,105,0.08)" },
                    "ytt": { badge: "Ultimate VIP", bg: "linear-gradient(180deg, #F29A0C 0%, #E97818 50%, #C8660A 100%)", glow: "rgba(242,154,12,0.25)", lightBg: "linear-gradient(180deg, rgba(255,248,238,0.95), rgba(255,237,213,0.75))", text: "#C8660A", border: "rgba(242,154,12,0.3)", accentBg: "rgba(242,154,12,0.1)" }
                  };

                  const EXTRA_PALETTES = [
                    { badge: "Custom Tier", bg: "linear-gradient(180deg, #C084FC 0%, #9333EA 50%, #7E22CE 100%)", glow: "rgba(147,51,234,0.22)", lightBg: "linear-gradient(180deg, rgba(250,245,255,0.95), rgba(243,232,255,0.75))", text: "#7E22CE", border: "rgba(147,51,234,0.28)", accentBg: "rgba(147,51,234,0.08)" },
                    { badge: "Special Plan", bg: "linear-gradient(180deg, #F472B6 0%, #DB2777 50%, #BE185D 100%)", glow: "rgba(219,39,119,0.22)", lightBg: "linear-gradient(180deg, rgba(253,242,248,0.95), rgba(252,231,243,0.75))", text: "#BE185D", border: "rgba(219,39,119,0.28)", accentBg: "rgba(219,39,119,0.08)" },
                    { badge: "Exclusive", bg: "linear-gradient(180deg, #2DD4BF 0%, #0D9488 50%, #0F766E 100%)", glow: "rgba(13,148,136,0.22)", lightBg: "linear-gradient(180deg, rgba(240,253,250,0.95), rgba(204,251,241,0.75))", text: "#0F766E", border: "rgba(13,148,136,0.28)", accentBg: "rgba(13,148,136,0.08)" }
                  ];

                  if (isInitialLoading && packages.length === 0) {
                    const SKELETON_TIERS = [
                      { name: "Free Trial", height: "45%" },
                      { name: "Basic Plan", height: "85%" },
                      { name: "Standard Plan", height: "65%" },
                      { name: "Premium Plan", height: "95%" },
                      { name: "YTT Plan", height: "40%" },
                    ];
                    return (
                      <div className="relative z-10 w-full animate-pulse">
                        {/* Header Title Bar Skeleton */}
                        <div className="flex items-center justify-between mb-6 relative z-10">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-zinc-200/80" />
                            <div className="flex flex-col gap-2">
                              <div className="h-5 w-72 rounded-lg bg-zinc-200/80" />
                              <div className="h-3.5 w-96 rounded bg-zinc-100/90" />
                            </div>
                          </div>
                          <div className="h-7 w-28 rounded-full bg-zinc-100 border border-zinc-200/60" />
                        </div>

                        {/* 5-Column Skeleton Grid */}
                        <div className="ad-hscroll-grid grid grid-cols-5 gap-3.5 items-end pt-2 pb-2">
                          {SKELETON_TIERS.map((sk, idx) => (
                            <div
                              key={idx}
                              className="rounded-[22px] p-4 flex flex-col justify-between border relative overflow-hidden"
                              style={{
                                height: 320,
                                background: "linear-gradient(180deg, rgba(255,255,255,0.85), rgba(248,245,238,0.7))",
                                borderColor: "rgba(235,225,210,0.8)",
                              }}
                            >
                              {/* Top pill badge skeleton */}
                              <div className="flex items-center justify-between">
                                <div className="h-4 w-14 rounded-full bg-zinc-200/70" />
                                <div className="h-3.5 w-8 rounded bg-zinc-100" />
                              </div>

                              {/* Title and Price skeleton */}
                              <div className="my-2">
                                <div className="h-4 w-20 rounded bg-zinc-200/70 mb-1.5" />
                                <div className="h-6 w-16 rounded-md bg-zinc-200/80" />
                              </div>

                              {/* Vertical Bar chart skeleton with staggered height */}
                              <div className="w-full flex-1 flex flex-col justify-end my-2">
                                <div
                                  className="w-full rounded-t-[14px] bg-gradient-to-t from-zinc-200/80 to-zinc-100/60"
                                  style={{ height: sk.height }}
                                />
                              </div>

                              {/* Subscriber counter bottom shimmer */}
                              <div className="pt-2 border-t border-zinc-200/60 flex items-center justify-between">
                                <div className="h-3.5 w-12 rounded bg-zinc-100" />
                                <div className="h-4 w-6 rounded-full bg-zinc-200/70" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  // Collect all unique tier names dynamically ONLY from active non-deleted packages
                  const activePkgs = packages.filter(p => p.isActive !== false);
                  const rawTierList = activePkgs.length > 0
                    ? ["Free Trial"]
                    : ["Free Trial", "Basic Plan", "Standard Plan", "Premium Plan", "YTT Plan"];
                  activePkgs.forEach(p => {
                    const pName = p.name || p.packageType || "";
                    if (pName && !rawTierList.some(t => t.toLowerCase() === pName.toLowerCase())) {
                      rawTierList.push(pName);
                    }
                  });

                  // Build distinct active paid users map (1 active paid subscription per unique user, keeping latest)
                  const distinctActivePaidUsersMap = new Map();
                  realSubscriptions.forEach(s => {
                    const userKey = s.userId || s.email || s.name;
                    if (!userKey) return;
                    const isPaid = (s.amountPaid > 0 || s.amount > 0) && (s.tier && !s.tier.toLowerCase().includes("free"));
                    const isActive = s.status === "Active" && (s.daysLeft === undefined || s.daysLeft > 0);
                    if (isPaid && isActive) {
                      if (!distinctActivePaidUsersMap.has(userKey) || Number(s.id || 0) > Number(distinctActivePaidUsersMap.get(userKey).id || 0)) {
                        distinctActivePaidUsersMap.set(userKey, s);
                      }
                    }
                  });
                  const distinctActivePaidSubs = Array.from(distinctActivePaidUsersMap.values());

                  const dynamicTiers = rawTierList.map((tName, idx) => {
                    const searchKey = tName.toLowerCase().replace(" plan", "").replace(" trial", "").trim();
                    const pkgMatch = packages.find(p => {
                      const pName = (p.name || "").toLowerCase();
                      const pType = (p.packageType || "").toLowerCase();
                      return pName.includes(searchKey) || pType.includes(searchKey);
                    });

                    const presetStyle = PRESET_TIER_STYLES[searchKey] || PRESET_TIER_STYLES[Object.keys(PRESET_TIER_STYLES).find(k => searchKey.includes(k))];
                    const style = presetStyle || EXTRA_PALETTES[idx % EXTRA_PALETTES.length];

                    const displayPrice = (() => {
                      if (tName === "Free Trial") return "₹0";
                      if (pkgMatch) {
                        const finalP = pkgMatch.discountedPrice || Math.round(pkgMatch.price * (1 - ((pkgMatch.discountPercentage || 0) / 100)));
                        return `₹${finalP.toLocaleString()}`;
                      }
                      if (tName.includes("Basic")) return "₹599";
                      if (tName.includes("Standard")) return "₹999";
                      if (tName.includes("Premium")) return "₹1,499";
                      if (tName.includes("YTT")) return "₹4,999";
                      return "₹0";
                    })();

                    const isFreeTier = tName.toLowerCase().includes("free") || tName.toLowerCase().includes("trial");

                    // Compute count: Free trial = 7 active unexpired trial users; Paid tiers = real distinct active subscribers (Total = 3)
                    const count = (() => {
                      if (isFreeTier) {
                        return (dbStats.freeTrialCount !== undefined && dbStats.freeTrialCount !== null)
                          ? Number(dbStats.freeTrialCount)
                          : 0;
                      }

                      // Check if backend returned planDistribution
                      if (Array.isArray(dbStats.planDistribution) && dbStats.planDistribution.length > 0) {
                        const norm = packageService.normalizeTierName(tName).toLowerCase();
                        const found = dbStats.planDistribution.find(d => {
                          const dNorm = packageService.normalizeTierName(d.tier || d.Tier || "").toLowerCase();
                          return dNorm === norm || dNorm.includes(norm) || norm.includes(dNorm);
                        });
                        if (found) return found.count ?? found.Count ?? 0;
                      }

                      return distinctActivePaidSubs.filter(s => matchTier(s, tName)).length;
                    })();

                    return {
                      name: tName,
                      badge: pkgMatch?.badgeTag || style.badge || "Tier",
                      price: displayPrice,
                      count,
                      ...style
                    };
                  });

                  const totalActiveUsers = dynamicTiers.reduce((a, b) => a + b.count, 0) || 1;
                  const maxCount = Math.max(1, ...dynamicTiers.map(t => t.count));
                  const totalTierCount = dynamicTiers.length;

                  return (
                    <>
                      {/* Ambient 3D Mesh Lighting Glow */}
                      <div className="absolute top-0 right-0 w-96 h-96 rounded-full pointer-events-none blur-3xl opacity-40"
                        style={{ background: "radial-gradient(circle, rgba(242,154,12,0.15) 0%, rgba(255,255,255,0) 70%)" }}/>

                      {/* Header Title Bar */}
                      <div className="ad-wrap flex items-center justify-between gap-3 mb-6 relative z-10">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-amber-500 to-amber-400 p-[1px] shadow-md">
                            <div className="w-full h-full rounded-[15px] bg-white flex items-center justify-center text-amber-600">
                              <FiZap size={19} className="animate-pulse"/>
                            </div>
                          </div>
                          <div>
                            <h3 className="font-black text-[19px] text-zinc-900 tracking-tight" style={{ fontFamily:"'Poppins',sans-serif" }}>
                              {totalTierCount}-Tier Subscription Package Performance
                            </h3>
                            <p className="text-[12px] text-zinc-500 font-semibold mt-0.5">
                              Live distribution of active subscribers & tier metrics across all {totalTierCount} plans
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 shadow-sm backdrop-blur-md">
                          <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"/>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"/>
                          </span>
                          <span className="text-[11px] font-black tracking-wider uppercase">Live</span>
                        </div>
                      </div>

                      <div className="relative z-10">
                        {/* Dynamic Integrated 3D Metallic Columns */}
                        <div className="ad-hscroll-grid grid gap-3.5 items-end pt-2 pb-2"
                          style={{ gridTemplateColumns: `repeat(${totalTierCount}, minmax(0, 1fr))` }}>
                          {dynamicTiers.map((t, idx) => {
                            const count = t.count;
                            const pct = totalActiveUsers > 0 ? Math.round((count / totalActiveUsers) * 100) : 0;
                            const barHeightPct = count > 0 ? Math.max(28, (count / maxCount) * 100) : 15;
                            const isCardSelected = tierFilter !== "all" && matchTier({ tier: t.name }, tierFilter);

                            return (
                              <motion.div key={t.name}
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.05 * idx, duration: 0.4 }}
                                whileHover={{ y: -5, scale: 1.04 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => handleTierCardClick(t.name)}
                                className={`flex flex-col items-center justify-end h-full gap-2 group cursor-pointer p-1.5 rounded-2xl transition-all ${
                                  isCardSelected ? "ring-2 ring-amber-500 scale-[1.03] shadow-lg bg-amber-500/10" : ""
                                }`}>
                                
                                {/* Top Badge & Floating Capsule */}
                                <div className="flex flex-col items-center gap-1 w-full z-20">
                                  <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border shadow-2xs truncate max-w-full text-center"
                                    style={{ background: t.accentBg, color: t.text, borderColor: t.border }}>
                                    {t.badge}
                                  </span>
                                  
                                  <div className="w-full py-1 px-2 rounded-xl bg-white/95 backdrop-blur-md shadow-sm border flex items-center justify-center gap-1.5 transition-transform duration-300 group-hover:scale-105"
                                    style={{ borderColor: t.border, boxShadow: `0 4px 14px ${t.glow}` }}>
                                    <span className="text-[14px] font-black leading-none" style={{ color: t.text }}>
                                      {count}
                                    </span>
                                    <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded-md bg-zinc-900 text-white">
                                      {pct}%
                                    </span>
                                  </div>
                                </div>

                                {/* 3D Glass Tube Bar Track */}
                                <div className="w-full rounded-[22px] relative overflow-hidden flex items-end p-1.5 transition-all duration-300 border group-hover:shadow-md"
                                  style={{
                                    height: 120,
                                    background: t.lightBg,
                                    borderColor: t.border,
                                    boxShadow: "inset 0 3px 8px rgba(0,0,0,0.04), inset 0 -2px 4px rgba(255,255,255,0.9)"
                                  }}>
                                  
                                  {/* 3D Gradient Tube Cylinder */}
                                  <motion.div className="w-full rounded-[16px] relative overflow-hidden flex flex-col justify-between"
                                    initial={{ height: 0 }}
                                    animate={{ height: `${barHeightPct}%` }}
                                    transition={{ type: "spring", stiffness: 220, damping: 20, delay: 0.06 * idx }}
                                    style={{
                                      background: count > 0 ? t.bg : "linear-gradient(180deg, rgba(200,200,200,0.4), rgba(160,160,160,0.2))",
                                      opacity: count > 0 ? 1 : 0.45,
                                      boxShadow: count > 0 ? `0 6px 18px ${t.glow}, inset 0 2px 4px rgba(255,255,255,0.8), inset 0 -4px 8px rgba(0,0,0,0.2)` : "none"
                                    }}>
                                    
                                    {/* Glass Top Flare */}
                                    <div className="w-full h-1.5 rounded-full bg-white/60 backdrop-blur-xs mt-1 mx-auto max-w-[75%]"
                                      style={{ boxShadow: "0 1px 3px rgba(255,255,255,0.9)" }}/>

                                    {/* Glossy Sheen Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/20 to-transparent pointer-events-none"/>
                                  </motion.div>
                                </div>

                                {/* Bottom Tier Label & Prominent Price Tag */}
                                <div className="text-center w-full pt-1 pb-0.5">
                                  <span className="text-[12px] font-black text-zinc-900 block truncate w-full tracking-tight mb-1"
                                    style={{ fontFamily: "'Poppins',sans-serif" }}>
                                    {t.name}
                                  </span>
                                  
                                  <div className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full bg-zinc-900 text-amber-400 font-extrabold text-[11px] border border-amber-500/30 shadow-xs">
                                    {t.price}
                                  </div>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </motion.div>

              {/* Payment Methods & Plan Distribution Summary (Spans 1 Column - ULTRA LUXURY 3D GLASS) */}
              <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.16 }}
                className="ad-card rounded-[32px] p-7 flex flex-col justify-between relative overflow-hidden"
                style={{
                  background: "linear-gradient(145deg, rgba(255,255,255,0.99), rgba(250,246,238,0.96))",
                  backdropFilter: "blur(24px)",
                  border: "1px solid rgba(235,225,210,0.85)",
                  boxShadow: "0 20px 50px rgba(0,0,0,0.05), inset 0 1px 1px #fff"
                }}>
                
                {/* Ambient 3D Radial Glow */}
                <div className="absolute top-0 left-0 w-64 h-64 rounded-full pointer-events-none blur-3xl opacity-35"
                  style={{ background: "radial-gradient(circle, rgba(242,154,12,0.15) 0%, rgba(255,255,255,0) 70%)" }}/>

                {(() => {
                  // Count payment methods based on latest unique user subscriptions:
                  const latestUserSubsMap = new Map();
                  realSubscriptions.forEach(s => {
                    const userKey = s.userId || s.email || s.name;
                    if (!userKey) return;
                    if (!latestUserSubsMap.has(userKey)) {
                      latestUserSubsMap.set(userKey, s);
                    } else {
                      const existing = latestUserSubsMap.get(userKey);
                      if (Number(s.id || 0) > Number(existing.id || 0)) {
                        latestUserSubsMap.set(userKey, s);
                      }
                    }
                  });
                  const latestUserSubs = Array.from(latestUserSubsMap.values()).filter(s => s.amountPaid > 0 || s.amount > 0 || (s.tier && s.tier.toLowerCase() !== "free trial"));

                  const onlineCount = latestUserSubs.filter(s => s.method === "Online" || (s.txId && !s.txId.startsWith("MANUAL"))).length;
                  const cashCount = latestUserSubs.filter(s => s.method === "Cash" || (s.txId && s.txId.startsWith("MANUAL"))).length;
                  const totalCount = latestUserSubs.length || (realSubscriptions.length > 0 ? 1 : 0);

                  const onlinePct = totalCount > 0 ? Math.round((onlineCount / totalCount) * 100) : (latestUserSubs.length > 0 ? 100 : 0);
                  const cashPct = totalCount > 0 ? 100 - onlinePct : 0;

                  const circumference = 301.6;
                  const onlineOffset = circumference - (onlinePct / 100) * circumference;
                  const cashOffset = circumference - (cashPct / 100) * circumference;

                  return (
                    <div className="relative z-10 flex flex-col justify-between h-full">
                      <div>
                        <div className="flex items-center gap-3 mb-1">
                          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-amber-500 to-amber-400 p-[1px] shadow-md">
                            <div className="w-full h-full rounded-[15px] bg-white flex items-center justify-center text-amber-600">
                              <FiCreditCard size={19}/>
                            </div>
                          </div>
                          <div>
                            <h3 className="font-black text-[18px] text-zinc-900 tracking-tight" style={{ fontFamily:"'Poppins',sans-serif" }}>
                              Payment Gateways
                            </h3>
                            <p className="text-[12px] text-zinc-500 font-semibold mt-0.5">
                              Live active user payment method ratio
                            </p>
                          </div>
                        </div>

                        {/* 3D Glass Donut Ring */}
                        <div className="flex justify-center my-4">
                          <div style={{ position:"relative", width:140, height:140 }}>
                            <svg viewBox="0 0 120 120" style={{ position:"absolute", inset:0, width:"100%", height:"100%", transform:"rotate(-90deg)", filter: "drop-shadow(0 8px 18px rgba(242,154,12,0.25))" }}>
                              {/* Background Track Circle */}
                              <circle cx="60" cy="60" r="46" fill="none" stroke="rgba(235,225,210,0.6)" strokeWidth="15"/>
                              
                              {/* Online Razorpay Ring */}
                              {totalCount > 0 && onlinePct > 0 && (
                                <motion.circle cx="60" cy="60" r="46" fill="none" stroke="url(#onlineGrad3)" strokeWidth="15"
                                  strokeDasharray={`${circumference}`}
                                  initial={{ strokeDashoffset: circumference }}
                                  animate={{ strokeDashoffset: onlineOffset }}
                                  transition={{ duration: 1.2, ease: "easeOut" }} strokeLinecap="round"/>
                              )}
                              
                              {/* Cash Ring */}
                              {totalCount > 0 && cashPct > 0 && (
                                <motion.circle cx="60" cy="60" r="46" fill="none" stroke="url(#cashGrad3)" strokeWidth="15"
                                  strokeDasharray={`${circumference}`}
                                  initial={{ strokeDashoffset: circumference }}
                                  animate={{ strokeDashoffset: cashOffset }}
                                  style={{ transform: `rotate(${(onlinePct / 100) * 360}deg)`, transformOrigin: "60px 60px" }}
                                  transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }} strokeLinecap="round"/>
                              )}

                              <defs>
                                <linearGradient id="onlineGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="#FBBF24" />
                                  <stop offset="100%" stopColor="#E97818" />
                                </linearGradient>
                                <linearGradient id="cashGrad3" x1="0%" y1="0%" x2="100%" y2="100%">
                                  <stop offset="0%" stopColor="#8BA47A" />
                                  <stop offset="100%" stopColor="#4A6838" />
                                </linearGradient>
                              </defs>
                            </svg>

                            {/* Center Glass Orb Capsule */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-center m-3 rounded-full bg-white/95 backdrop-blur-md border border-white shadow-md"
                              style={{ boxShadow: "inset 0 2px 5px rgba(255,255,255,1), 0 6px 18px rgba(0,0,0,0.05)" }}>
                              <span className="text-[9px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200/80 mb-0.5">
                                RATIO
                              </span>
                              <span className="font-black text-[24px] text-zinc-900 leading-none">{totalCount}</span>
                              <span className="text-[9px] uppercase tracking-wider font-black text-zinc-400 mt-0.5">Paid User</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 3D Legend Metric Pods */}
                      <div className="space-y-2.5 pt-3 border-t border-zinc-200/80">
                        {/* Online (Razorpay) Card */}
                        <motion.div whileHover={{ y: -2 }} className="p-3 rounded-2xl bg-white/95 backdrop-blur-md border border-amber-200/90 shadow-2xs">
                          <div className="flex items-center justify-between text-[12px] font-black mb-1.5">
                            <span className="flex items-center gap-2 text-zinc-900">
                              <span className="w-3.5 h-3.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-600 shadow-xs flex items-center justify-center text-[8px] text-white">✓</span>
                              Online (Razorpay)
                            </span>
                            <span className="text-amber-900 bg-amber-100/70 px-2.5 py-0.5 rounded-full border border-amber-300 font-extrabold text-[11px]">
                              {onlinePct}% ({onlineCount} User)
                            </span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden p-0.5 border border-zinc-200/50">
                            <motion.div className="h-full rounded-full"
                              initial={{ width: 0 }} animate={{ width: `${onlinePct}%` }}
                              transition={{ duration: 1 }} style={{ background: "linear-gradient(90deg,#FBBF24,#E97818)" }} />
                          </div>
                        </motion.div>

                        {/* Cash (Manual) Card */}
                        <motion.div whileHover={{ y: -2 }} className="p-3 rounded-2xl bg-white/95 backdrop-blur-md border border-emerald-200/90 shadow-2xs">
                          <div className="flex items-center justify-between text-[12px] font-black mb-1.5">
                            <span className="flex items-center gap-2 text-zinc-900">
                              <span className="w-3.5 h-3.5 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 shadow-xs flex items-center justify-center text-[8px] text-white">✓</span>
                              Cash (Manual)
                            </span>
                            <span className="text-emerald-900 bg-emerald-100/70 px-2.5 py-0.5 rounded-full border border-emerald-300 font-extrabold text-[11px]">
                              {cashPct}% ({cashCount} User)
                            </span>
                          </div>
                          <div className="w-full h-2 rounded-full bg-zinc-100 overflow-hidden p-0.5 border border-zinc-200/50">
                            <motion.div className="h-full rounded-full"
                              initial={{ width: 0 }} animate={{ width: `${cashPct}%` }}
                              transition={{ duration: 1 }} style={{ background: "linear-gradient(90deg,#8BA47A,#4A6838)" }} />
                          </div>
                        </motion.div>
                      </div>
                    </div>
                  );
                })()}
              </motion.div>
            </div>

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* 1. PENDING CASH ACTIVATIONS TABLE (ABOVE MAIN TABLE)              */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {(() => {
              const pendingItems = pendingCashSubscriptions;
              const pendingCount = pendingItems.length || (dbStats.pendingCashActivations || []).length || (dbStats.pendingCount || 0);

              if (pendingCount === 0 && pendingItems.length === 0) return null;

              return (
                <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.18 }}
                  className="ad-card rounded-[28px] p-6 mb-6 relative overflow-hidden border border-amber-400/40"
                  style={{ background: "linear-gradient(145deg, rgba(255,251,240,0.98), rgba(254,243,199,0.45))", boxShadow: "0 16px 40px rgba(245,158,11,0.08)" }}>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-amber-700 bg-amber-100 border border-amber-300 shadow-sm shrink-0">
                        <FiAlertCircle size={20}/>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-black text-[17px] text-zinc-900 tracking-tight" style={{ fontFamily:"'Poppins',sans-serif" }}>
                            Pending Cash Activations
                          </h3>
                          <span className="text-[10px] font-black uppercase tracking-wider text-white bg-amber-600 px-2.5 py-0.5 rounded-full shadow-xs animate-pulse">
                            {pendingCount} URGENT
                          </span>
                        </div>
                        <p className="text-[12px] text-amber-900/80 font-medium">
                          Users who paid cash awaiting one-click manual activation
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <motion.button whileHover={{ scale:1.02 }} whileTap={{ scale:0.97 }}
                        onClick={() => setShowActivateModal(true)}
                        className="h-9 px-4 rounded-full text-white text-[12px] font-bold border-none cursor-pointer flex items-center gap-1.5 shadow-sm"
                        style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})` }}>
                        <FiPlus size={13}/> Manual Activation
                      </motion.button>
                    </div>
                  </div>

                  {pendingItems.length > 0 ? (
                    <div className="ad-table rounded-[20px] overflow-hidden border border-amber-200/80 bg-white/95 shadow-sm">
                      <table style={{ width:"100%", borderCollapse:"collapse" }}>
                        <thead>
                          <tr className="bg-amber-50/70 border-b border-amber-200/60">
                            {["User / Subscriber", "Requested Plan", "Amount (₹)", "Payment Method", "Submitted Date", "Action"].map((h, hIdx) => (
                              <th key={h} className={`text-[10px] font-black uppercase tracking-[0.9px] text-amber-900 py-2.5 px-4 ${hIdx === 5 ? "text-right" : "text-left"}`}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pendingItems.map((sub, sIdx) => {
                            const userName = sub.name || sub.userFullName || "Subscriber";
                            const userEmail = sub.email || sub.userEmail || "";
                            const planName = sub.tier || sub.packageName || "Standard Plan";
                            const amt = sub.amount ?? sub.amountPaid ?? 0;
                            const isSubmitting = actionLoadingId === sub.id;

                            return (
                              <tr key={sub.id || sIdx} className="border-b border-amber-100 hover:bg-amber-50/40 transition-colors">
                                <td className="py-3 px-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-amber-100 text-amber-800 font-black text-[12px] border border-amber-300 shrink-0">
                                      {sub.profileImageUrl ? (
                                        <img src={sub.profileImageUrl} alt="" className="w-full h-full object-cover"/>
                                      ) : (
                                        userName.charAt(0).toUpperCase()
                                      )}
                                    </div>
                                    <div className="truncate min-w-0">
                                      <p className="text-[13px] font-bold text-zinc-900 truncate">{userName}</p>
                                      <p className="text-[11px] text-zinc-500 truncate">{userEmail}</p>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 border border-amber-200">
                                    {planName}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-[13px] font-black text-zinc-900">
                                    ₹{Number(amt).toLocaleString()}
                                  </span>
                                </td>
                                <td className="py-3 px-4">
                                  <span className="text-[11px] font-bold text-zinc-700 bg-zinc-100 px-2 py-0.5 rounded-md border border-zinc-200">
                                    Cash / Manual
                                  </span>
                                </td>
                                <td className="py-3 px-4 text-[12px] text-zinc-600 font-semibold">
                                  {sub.start || sub.startDate || "Today"}
                                </td>
                                <td className="py-3 px-4 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                                      disabled={isSubmitting}
                                      onClick={() => handleOneClickActivateCash(sub)}
                                      className="px-3.5 py-1.5 rounded-full text-white text-[11px] font-bold border-none cursor-pointer shadow-xs flex items-center gap-1.5"
                                      style={{ background: `linear-gradient(135deg,${T.greenDk || '#16A34A'},#15803D)` }}>
                                      <FiCheck size={12}/> {isSubmitting ? "Activating..." : "Activate Now"}
                                    </motion.button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-4 rounded-2xl bg-white/70 border border-amber-200 text-center text-[12px] font-bold text-amber-900">
                      ⚡ {pendingCount} offline cash subscription request(s) waiting for manual verification. Click "Manual Activation" above to activate.
                    </div>
                  )}
                </motion.div>
              );
            })()}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* 2. EXPIRED SUBSCRIPTIONS TABLE (ABOVE MAIN TABLE)                 */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            {expiredSubscriptions.length > 0 && (
              <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.2 }}
                className="ad-card rounded-[28px] p-6 mb-6 relative overflow-hidden border border-red-200/80"
                style={{ background: "linear-gradient(145deg, rgba(254,242,242,0.96), rgba(255,255,255,0.95))", boxShadow: "0 16px 36px rgba(226,75,74,0.06)" }}>
                
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-red-600 bg-red-100 border border-red-200 shadow-sm shrink-0">
                      <FiClock size={20}/>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-black text-[17px] text-zinc-900 tracking-tight" style={{ fontFamily:"'Poppins',sans-serif" }}>
                          Expired Subscriptions
                        </h3>
                        <span className="text-[10px] font-black uppercase tracking-wider text-white bg-red-600 px-2.5 py-0.5 rounded-full shadow-xs">
                          {expiredSubscriptions.length} EXPIRED
                        </span>
                      </div>
                      <p className="text-[12px] text-zinc-500 font-medium">
                        Users whose subscription term has ended — one-click renew or extend access
                      </p>
                    </div>
                  </div>
                </div>

                <div className="ad-table rounded-[20px] overflow-hidden border border-red-100 bg-white/95 shadow-sm max-h-[300px] overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead>
                      <tr className="bg-red-50/60 border-b border-red-100 sticky top-0 bg-white">
                        {["User / Subscriber", "Previous Plan", "Expiry Date", "Days Expired", "Action"].map((h, hIdx) => (
                          <th key={h} className={`text-[10px] font-black uppercase tracking-[0.9px] text-red-900 py-2.5 px-4 ${hIdx === 4 ? "text-right" : "text-left"}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {expiredSubscriptions.slice(0, 8).map((sub, sIdx) => {
                        const userName = sub.name || sub.userFullName || "Subscriber";
                        const userEmail = sub.email || sub.userEmail || "";
                        const planName = sub.tier || sub.packageName || "Standard Plan";
                        const expDate = sub.end || sub.endDate || "Past Date";
                        const isSubmitting = actionLoadingId === sub.id;

                        return (
                          <tr key={sub.id || sIdx} className="border-b border-red-50 hover:bg-red-50/30 transition-colors">
                            <td className="py-2.5 px-4">
                              <div className="flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-full overflow-hidden flex items-center justify-center bg-zinc-200 text-zinc-700 font-bold text-[11px] shrink-0">
                                  {sub.profileImageUrl ? (
                                    <img src={sub.profileImageUrl} alt="" className="w-full h-full object-cover"/>
                                  ) : (
                                    userName.charAt(0).toUpperCase()
                                  )}
                                </div>
                                <div className="truncate min-w-0">
                                  <p className="text-[12px] font-bold text-zinc-900 truncate">{userName}</p>
                                  <p className="text-[10px] text-zinc-400 truncate">{userEmail}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-2.5 px-4">
                              <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200">
                                {planName}
                              </span>
                            </td>
                            <td className="py-2.5 px-4 text-[12px] text-zinc-700 font-medium">
                              {expDate}
                            </td>
                            <td className="py-2.5 px-4">
                              <span className="text-[11px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-md border border-red-200">
                                Expired
                              </span>
                            </td>
                            <td className="py-2.5 px-4 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                                  onClick={() => setActionModal({ type: "extend", sub })}
                                  className="px-3 py-1.5 rounded-full text-white text-[11px] font-bold border-none cursor-pointer shadow-xs flex items-center gap-1.5"
                                  style={{ background: `linear-gradient(135deg,${T.olive},${T.olive2})` }}
                                  title="Admin decides exact duration days to extend/renew">
                                  <FiCalendar size={11}/> Renew / Extend
                                </motion.button>

                                <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                                  onClick={() => setActionModal({ type: "upgrade", sub })}
                                  className="px-3 py-1.5 rounded-full text-white text-[11px] font-bold border-none cursor-pointer shadow-xs flex items-center gap-1.5"
                                  style={{ background: `linear-gradient(135deg,${T.orange},${T.orange2})` }}
                                  title="Upgrade or change plan tier for expired subscription">
                                  <FiArrowUp size={11}/> Upgrade Plan
                                </motion.button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* ═════════════════════════════════════════════════════════════════ */}
            {/* 3. FILTERS & TABLE TOOLBAR SECTION                                */}
            {/* ═════════════════════════════════════════════════════════════════ */}
            <div id="subscriptions-table-section" className="rounded-[24px] p-4 mb-4 relative z-30 overflow-visible"
              style={{ background:"rgba(255,255,255,0.92)", backdropFilter:"blur(20px)", border:`1px solid ${T.border}`, boxShadow:"0 10px 30px rgba(0,0,0,0.03)" }}>
              
              {/* Top Row: Search Input (Left) + Status Filter Tabs (Right) */}
              <div className="flex flex-col xl:flex-row items-stretch xl:items-center justify-between gap-3 pb-3.5 mb-3.5 border-b border-zinc-200/60">
                {/* Search Bar */}
                <div className="flex items-center gap-2.5 rounded-full px-4 py-2 flex-1 max-w-md bg-white border border-amber-950/15 shadow-2xs">
                  <FiSearch size={15} style={{ color: T.orange }} className="shrink-0"/>
                  <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search user name, email, plan..."
                    className="w-full bg-transparent border-none outline-none text-[13px] text-[#1F1F1F] font-semibold placeholder:text-zinc-400"
                    style={{ fontFamily:"'Inter',sans-serif" }}
                  />
                  {search && (
                    <button onClick={() => setSearch("")} className="border-none bg-transparent cursor-pointer text-zinc-400 hover:text-zinc-700">
                      <FiX size={14}/>
                    </button>
                  )}
                </div>

                {/* Status Filter Tabs (Pills) */}
                <div className="flex items-center gap-1.5 p-1 rounded-full bg-zinc-100/80 border border-zinc-200/80 overflow-x-auto shadow-inner">
                  {[
                    { id: "all", label: "All Subscriptions", count: statusCounts.all },
                    { id: "active", label: "Active", count: statusCounts.active, dot: "#16A34A" },
                    { id: "expiring_soon", label: "Expiring Soon", count: statusCounts.expiring_soon, dot: "#F59E0B" },
                    { id: "cancelled", label: "Cancelled", count: statusCounts.cancelled, dot: "#E24B4A" },
                    { id: "expired", label: "Expired", count: statusCounts.expired, dot: "#71717A" },
                  ].map(tab => {
                    const isSelected = statusFilter === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => { setStatusFilter(tab.id); setCurrentPage(1); }}
                        className={`px-3.5 py-1.5 rounded-full text-[12px] font-bold cursor-pointer transition-all flex items-center gap-1.5 whitespace-nowrap border-none ${
                          isSelected
                            ? "bg-amber-500 text-white shadow-xs"
                            : "bg-transparent text-zinc-600 hover:bg-white/70"
                        }`}
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {tab.dot && !isSelected && (
                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: tab.dot }} />
                        )}
                        <span>{tab.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-black ${
                          isSelected ? "bg-white/25 text-white" : "bg-zinc-200/90 text-zinc-700"
                        }`}>
                          {tab.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Bottom Row: Parameter Dropdowns (Plan, Method, Period) */}
              <div className="flex flex-wrap items-center justify-between gap-3 relative z-40 overflow-visible">
                <div className="ad-filters ad-full flex flex-wrap items-center gap-2.5">
                  <span className="ad-filters-full text-[10px] font-black uppercase tracking-wider text-zinc-400 mr-1 flex items-center gap-1">
                    <FiFilter size={12}/> Filters:
                  </span>
                  <CustomDropdown
                    value={tierFilter}
                    onChange={setTierFilter}
                    options={uniqueTierOptions}
                    label="Plan"
                    icon={FiTag}
                    fullWidth={false}
                  />
                  <CustomDropdown
                    value={methodFilter}
                    onChange={setMethodFilter}
                    options={["all", "Online", "Cash"]}
                    label="Method"
                    icon={FiCreditCard}
                    fullWidth={false}
                  />
                  <CustomDropdown
                    value={dateRangeFilter}
                    onChange={setDateRangeFilter}
                    options={[
                      { label: "All Time Users", value: "all" },
                      { label: "This Month Users", value: "this_month" },
                      { label: "2 Months Users", value: "2_months" },
                      { label: "3 Months Users", value: "3_months" },
                      { label: "6 Months Users", value: "6_months" },
                      { label: "1 Year Users", value: "1_year" }
                    ]}
                    label="User Period"
                    icon={FiClock}
                    fullWidth={false}
                  />
                </div>

                {/* Reset Filters Action */}
                {(tierFilter !== "all" || methodFilter !== "all" || dateRangeFilter !== "all" || statusFilter !== "all" || search) && (
                  <button
                    onClick={() => {
                      setTierFilter("all");
                      setMethodFilter("all");
                      setDateRangeFilter("all");
                      setStatusFilter("all");
                      setSearch("");
                    }}
                    className="text-[11px] font-bold text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100/80 px-3 py-1.5 rounded-full border border-amber-200/80 cursor-pointer flex items-center gap-1 transition-all shadow-2xs"
                  >
                    <FiX size={12}/> Reset All Filters
                  </button>
                )}
              </div>
            </div>

            {/* Subscriptions Table */}
            <motion.div initial={{ opacity:0, y:14 }} animate={{ opacity:1, y:0 }} transition={{ delay:0.22 }}
              className="ad-table rounded-[24px] overflow-hidden shadow-sm relative z-10" style={glass}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    {[["name","User"],["tier","Plan"],["amount","Amount"],["method","Method"],["status","Status"],["start","Start"],["end","Expiry"],["daysLeft","Days Left"]].map(([c,l]) => (
                      <SortTH key={c} col={c} label={l}/>
                    ))}
                    <th style={{ textAlign:"right", padding:"10px 14px", fontSize:10, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.9px", color:T.light, borderBottom:`1px solid rgba(239,229,216,0.6)` }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSubscriptions.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-10 text-center text-zinc-500 font-bold text-[13px]">
                        No subscription records found matching current filters.
                      </td>
                    </tr>
                  ) : (
                    paginatedSubscriptions.map((s, i) => {
                      const userName = s.name || s.userFullName || "User";
                      const userEmail = s.email || s.userEmail || "";
                      const planTier = s.tier || s.packageName || "Basic";
                      const paidAmt = s.amount ?? s.amountPaid ?? 0;
                      const payMethod = s.method || (s.txId?.startsWith("MANUAL") ? "Cash" : "Online");
                      const subStatus = s.status || "Active";

                      // Dynamic matching of admin-updated Package duration:
                      const matchedPkg = packages.find(p => (s.packageId && p.id === s.packageId) || matchTier(s, p.name));
                      const totalTierDays = matchedPkg?.durationInDays || matchedPkg?.durationDays || s.daysTotal || 30;

                      const daysRemaining = (() => {
                        if (s.end && s.end !== "Unlimited") {
                          const endDt = new Date(s.end);
                          if (!isNaN(endDt.getTime())) {
                            const now = new Date();
                            const diff = Math.ceil((endDt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                            return diff > 0 ? diff : 0;
                          }
                        }
                        if (s.daysLeft !== undefined && s.daysLeft !== null) {
                          return s.daysLeft;
                        }
                        return totalTierDays;
                      })();

                      const urgency = daysRemaining < 7 ? T.red : daysRemaining < 14 ? T.amber : T.mid;

                      return (
                        <motion.tr key={s.id || i} initial={{ opacity:0, x:-6 }} animate={{ opacity:1, x:0 }}
                          transition={{ delay:Math.min(i*0.03, 0.3) }} onClick={() => setDrawerSub(s)}
                          className="cursor-pointer"
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(107,138,90,0.04)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                            <div className="flex items-center gap-2.5">
                              <UserAvatar sub={s.initials ? s : { initials: userName.substring(0,2).toUpperCase(), grad: AVATAR_GRADS[i % 5] }}/>
                              <div>
                                <p className="text-[13px] font-bold text-[#1F1F1F]">{userName}</p>
                                <p className="text-[11px]" style={{ color:T.light }}> {userEmail}</p>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                            <div className="flex flex-col items-start gap-1">
                              <TierPill tier={planTier}/>
                              <span className="text-[9px] font-extrabold text-amber-800 bg-amber-100/90 px-1.5 py-0.5 rounded border border-amber-300/50">
                                {totalTierDays} Days
                              </span>
                            </div>
                          </td>
                          <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                            <span className="text-[13px] font-black text-[#1F1F1F]">₹{paidAmt.toLocaleString()}</span>
                          </td>
                          <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full"
                              style={{ background:"rgba(255,255,255,0.9)", border:`1px solid ${T.border}`, color:payMethod==="Online"?T.orange:payMethod==="Cash"?T.olive:"#534AB7" }}>
                              {payMethod}
                            </span>
                          </td>
                          <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}><StatusPill status={subStatus}/></td>
                          <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)`, fontSize:12, color:T.muted }}>{s.start}</td>
                          <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)`, fontSize:12, color:T.muted }}>{s.end}</td>
                          <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                            <p className="text-[13px] font-black" style={{ color:urgency }}>{daysRemaining > 0 ? `${daysRemaining}d` : "Expired"}</p>
                            <p className="text-[9px] font-bold text-zinc-400">{totalTierDays}d total</p>
                          </td>
                          <td style={{ padding:"12px 14px", borderBottom:`1px solid rgba(239,229,216,0.35)` }}>
                            <div className="flex items-center gap-1.5 justify-end">
                              <motion.button whileHover={{ y:-1 }} onClick={e => { e.stopPropagation(); setActionModal({ type: "extend", sub: s }); }}
                                className="px-2.5 py-1 rounded-full text-[11px] font-extrabold border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 flex items-center gap-1 shadow-2xs cursor-pointer"
                                title={`Extend subscription duration for ${s.name}`}>
                                <FiCalendar size={12}/> Extend
                              </motion.button>

                              <motion.button whileHover={{ y:-1 }} onClick={e => { e.stopPropagation(); setActionModal({ type: "upgrade", sub: s }); }}
                                className="px-2.5 py-1 rounded-full text-[11px] font-extrabold border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 flex items-center gap-1 shadow-2xs cursor-pointer"
                                title={`Upgrade or change plan for ${s.name}`}>
                                <FiArrowUp size={12}/> Upgrade
                              </motion.button>

                              <motion.button whileHover={{ y:-1 }} onClick={e => { e.stopPropagation(); setDrawerSub(s); }}
                                className="w-7 h-7 rounded-[9px] flex items-center justify-center border cursor-pointer bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-100 shadow-2xs"
                                title="View Subscription Details">
                                <FiEye size={13}/>
                              </motion.button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {/* Table Pagination Suite */}
              <PaginationSuite
                currentPage={currentPage}
                totalPages={totalPagesCount}
                totalItems={filtered.length}
                pageSize={ITEMS_PER_PAGE}
                itemLabel="subscriptions"
                onPageChange={(p) => setCurrentPage(p)}
              />
            </motion.div>
          </div>
        )}

        {/* Detail drawer */}
        <AnimatePresence>
          {drawerSub && (
            <SubDrawer
              sub={drawerSub}
              packages={packages}
              matchTier={matchTier}
              allUserSubs={realSubscriptions.filter(s =>
                (s.userId && drawerSub.userId && s.userId === drawerSub.userId) ||
                (s.email && drawerSub.email && s.email.toLowerCase() === drawerSub.email.toLowerCase()) ||
                (s.name && drawerSub.name && s.name.toLowerCase() === drawerSub.name.toLowerCase())
              )}
              onClose={() => setDrawerSub(null)}
              onAction={(type, targetSub) => setActionModal({ type, sub: targetSub })}
              showToast={showToast}
            />
          )}
        </AnimatePresence>

        {/* Admin Action Modals */}
        <AnimatePresence>
          {actionModal?.type === "extend" && (
            <ExtendSubModal
              sub={actionModal.sub}
              onClose={() => setActionModal(null)}
              onSuccess={() => loadSubscriptionsData()}
              showToast={showToast}
            />
          )}
          {actionModal?.type === "upgrade" && (
            <UpgradeSubModal
              sub={actionModal.sub}
              packages={packages}
              onClose={() => setActionModal(null)}
              onSuccess={() => loadSubscriptionsData()}
              showToast={showToast}
            />
          )}
          {actionModal?.type === "invoice" && (
            <InvoiceModal
              sub={actionModal.sub}
              onClose={() => setActionModal(null)}
            />
          )}
          {actionModal?.type === "refund" && (
            <RefundSubModal
              sub={actionModal.sub}
              onClose={() => setActionModal(null)}
              onSuccess={() => loadSubscriptionsData()}
              showToast={showToast}
            />
          )}
          {actionModal?.type === "cancel" && (
            <CancelSubModal
              sub={actionModal.sub}
              onClose={() => setActionModal(null)}
              onSuccess={() => loadSubscriptionsData()}
              showToast={showToast}
            />
          )}
        </AnimatePresence>

        {/* Activation modal */}
        <AnimatePresence>
          {showActivateModal && (
            <ActivateModal
              packages={packages}
              onClose={() => setShowActivateModal(false)}
              onSuccess={() => { loadSubscriptionsData(); showToast("Subscription assigned / updated successfully!"); }}
              showToast={showToast}
            />
          )}
        </AnimatePresence>

        {/* Package modal */}
        <AnimatePresence>
          {showPkgModal && (
            <PackageModal
              pkg={editingPkg}
              existingPackages={packages}
              onClose={() => setShowPkgModal(false)}
              onSuccess={() => { loadSubscriptionsData(); }}
              showToast={showToast}
            />
          )}
        </AnimatePresence>

        {/* Custom Luxury Confirmation Modal for Package Actions */}
        <AnimatePresence>
          {pkgConfirmModal.isOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/65 backdrop-blur-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.93, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.93, y: 15 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="relative w-full max-w-md bg-white rounded-[28px] border border-zinc-200/80 p-6 sm:p-7 shadow-[0_25px_70px_rgba(0,0,0,0.25)] overflow-hidden"
              >
                {/* Top Accent Line */}
                <div
                  className={`absolute top-0 left-0 right-0 h-[3.5px] ${
                    pkgConfirmModal.isDanger
                      ? "bg-gradient-to-r from-transparent via-red-500 to-transparent"
                      : "bg-gradient-to-r from-transparent via-amber-500 to-transparent"
                  }`}
                />

                {/* Close Button */}
                <button
                  type="button"
                  onClick={() => setPkgConfirmModal((prev) => ({ ...prev, isOpen: false }))}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-500 hover:text-zinc-800 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <FiX size={15} />
                </button>

                {/* Header Icon + Title */}
                <div className="flex items-center gap-3.5 mb-3.5">
                  <div
                    className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${
                      pkgConfirmModal.isDanger
                        ? "bg-red-50 text-red-600 border border-red-200 shadow-[0_4px_12px_rgba(239,68,68,0.15)]"
                        : "bg-amber-50 text-amber-600 border border-amber-200 shadow-[0_4px_12px_rgba(245,158,11,0.15)]"
                    }`}
                  >
                    {pkgConfirmModal.actionType === "delete" ? (
                      <FiTrash2 size={18} />
                    ) : (
                      <FiAlertCircle size={18} />
                    )}
                  </div>
                  <div>
                    <h3 className="text-[17px] font-black text-zinc-900 leading-snug">
                      {pkgConfirmModal.title}
                    </h3>
                    <p className="text-[11px] text-zinc-500 font-medium">
                      Super Admin Authorization Required
                    </p>
                  </div>
                </div>

                {/* Package Name Tag */}
                {pkgConfirmModal.packageName && (
                  <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-3 mb-3.5">
                    <p className="text-[10px] uppercase tracking-wider font-extrabold text-zinc-400 mb-0.5">
                      Target Package Tier
                    </p>
                    <p className="text-[13px] font-bold text-zinc-800">
                      {pkgConfirmModal.packageName}
                    </p>
                  </div>
                )}

                {/* Description */}
                <p className="text-[12px] text-zinc-600 leading-relaxed mb-6 font-medium">
                  {pkgConfirmModal.message}
                </p>

                {/* Buttons */}
                <div className="flex items-center justify-end gap-2.5">
                  <button
                    type="button"
                    onClick={() => setPkgConfirmModal((prev) => ({ ...prev, isOpen: false }))}
                    className="px-4 py-2 rounded-full text-[12px] font-bold text-zinc-600 hover:bg-zinc-100 border border-zinc-200 transition-all cursor-pointer"
                  >
                    {pkgConfirmModal.cancelText}
                  </button>
                  <button
                    type="button"
                    onClick={handleExecutePkgAction}
                    className={`px-5 py-2 rounded-full text-white font-bold text-[12px] shadow-sm hover:shadow-md transition-all cursor-pointer flex items-center gap-1.5 ${
                      pkgConfirmModal.isDanger
                        ? "bg-red-600 hover:bg-red-700 shadow-red-500/20"
                        : "bg-amber-600 hover:bg-amber-700 shadow-amber-500/20"
                    }`}
                  >
                    {pkgConfirmModal.actionType === "delete" && <FiTrash2 size={12} />}
                    <span>{pkgConfirmModal.confirmText}</span>
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  </AdminDashboardLayout>
);
}